/**
 * voice-v8 — „Mein Alltag"-Ansicht (Task 5, pre.988).
 *
 * Reiter/Sheet mit der Liste der Alltags-Einträge (neueste zuerst).
 * Pro Tag: Tages-Rollup oben, darunter die Einträge mit Zeit, Digest
 * (Summary + To-dos), aufklappbarem Roh-Transkript und Audio-Player.
 *
 * Pattern: analog material-view.ts (gleiche Sheet-/Karten-Struktur,
 * page-routes-Deep-Link, lazy import in app.ts). DRY: Audio via
 * eigenem buildAudioPlayer (direkte Binär-Files), der dieselben
 * .player-bar-Klassen wie buildPlayerBar in app.ts verwendet — kein
 * zirkulärer Import, visuell identisch.
 */

import {
  fetchAlltag,
  fetchAlltagStatus,
  deleteAlltagEntry,
  regenerateAlltagRollup,
  getSpeakerId,
  startSpeakerId,
  nameSpeaker,
  fetchOffeneStimmen,
  type OffeneStimmeZeile,
  fetchAlltagPlaces,
  fetchSpeakers,
  renameSpeaker,
  deleteSpeaker,
  dayStatusTeile,
  type AlltagDay,
  type AlltagEntry,
  type AlltagNow,
  type AlltagStatusDay,
  type PlaceCluster,
  type Speaker,
  type SpeakerData,
} from "./alltag-api.js";
import {
  buildPageHeader,
  buildSheetHeader,
  confirmSheet,
  requestMenuClose , buildLoadingHint } from "./sheet-ui.js";
// 2026-07-14 (Konsolidierung): EIN geteilter Player fuer die ganze App
import { buildAudioPlayer, durationFromRange, formatClock } from "./audio-player.js";
import { getViewerPlayerBuilder, type ViewerNarrationPlayer } from "./project-file-viewer.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { vorleseStimme } from "./settings.js";
import { baueHaltePauseKnopf, type PauseModus } from "./halte-pause-knopf.js";
import { gesteuert } from "./ruckel-schalter.js";
import {
  setAlwaysDayRecording,
  isRecorderAvailable,
  kannAufzeichnen,
  startRecording,
  stopRecording,
  stopRecordingForUser,
  pauseRecorderForUser,
  resumeRecorderForUser,
  raeumeTonFuerAufnahme,
  getRecorderState,
  getUploadInfo,
  getRecordingStartedAt,
  getRecordingPausedAt,
  offenePausenGruende,
  observeRecorder,
  syncFromNative,
  getLastUploadAt,
  pushAlltagWidgetInfo,
  type RecorderState,
} from "./native-recorder.js";
import { laufText, pauseAnzeige, pauseText } from "./pause-besitzer.js";
import { weckeBeiUnfreiwilligerPause } from "./aufnahme-wache.js";

let sheetEl: HTMLElement | null = null;
// ID des „Mein Alltag"-Projekts (aus /api/alltag) — für den Kapitel-Player,
// der Abschnitte über /api/projects/<id>/narrate vertont.
let alltagProjectId = "";
/*
 * Ticket 2698: der oberste Player der Liste = der neueste Eintrag (neueste
 * Tage zuerst, im Tag die späteste Aufnahme zuerst). Gemerkt beim Bauen,
 * nicht im DOM gesucht — seit pre.3620 je Tag (`ersterSpielerJeTag`), weil ein
 * zugeklappter Tag erst beim Aufklappen gebaut wird.
 */
let spielerImBau: ViewerNarrationPlayer | null = null;
const ersterSpielerJeTag = new WeakMap<HTMLElement, ViewerNarrationPlayer>();
/** Die Player der gezeichneten Liste — wer beim Schließen noch spielt, hält die Liste (s. `leereVersteckteListe`). */
const listenSpieler = new Set<ViewerNarrationPlayer>();
/** Tage, in denen mindestens eine Aufnahme eine Vorlese-Fassung hat (aus den Daten, ohne zu bauen). */
const tagHatVorlesen = new WeakMap<HTMLElement, boolean>();
/*
 * Ein zugeklappter Tag trägt seinen Inhalt erst, wenn er aufgeht. (02.10.2026, pre.3620)
 *
 * Journal 02.10., 22:03–22:16, iPhone: In den Einstellungen scrollte Martin `ziel=.sheet-card`
 * mit `hoehe=15161`, dabei `knoten=44.936` statt ~2.000, Bildzeit-Median bis 134 ms — und der Chat
 * danach 83–113 ms. Das war „Mein Alltag": `div.page-body>div.alltag-list` mit 83 Tagen (der
 * Messfühler `[dom-wachstum]` nannte sie), JEDER Tag mit seinem ganzen Inhalt (Karten, Player,
 * Abschnitte) im DOM, auch zugeklappt — gezeichnet nur die Kopfzeilen, gezählt alles. Und beim
 * Schließen wurde die Seite nur versteckt: die Knoten blieben bis zum Neuladen, und jede fertige
 * Aufnahme baute die versteckte Liste neu (16:38–17:10 im Hintergrund von 1.600 auf 44.400).
 * Jetzt: Inhalt eines Tages erst beim Aufklappen (Tipp auf die Kopfzeile, vor dem Umschalten),
 * beim Schließen fällt die Liste weg (außer ein Player spielt noch — dann, sobald er still ist),
 * und eine versteckte Seite wird nicht neu gebaut.
 */
const baueTag = new WeakMap<HTMLElement, () => void>();
/** Die laufende Darstellung der Liste — wer den neuesten Player braucht, wartet darauf. */
let darstellung: Promise<void> = Promise.resolve();

const AUFNAHME_HAELT_HINWEIS =
  "Aufnahme beendet. Sie startet nicht von selbst wieder \u2014 tipp auf \u201eAufnehmen\u201c, wenn du weitermachen willst.";

function hinweisAufnahmeHaelt(): void {
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", {
      detail: { message: AUFNAHME_HAELT_HINWEIS, ms: 6000 },
    }),
  );
}

/** Bereitet TTS für einen Text vor → liefert die /api/tts/stream-URL (für buildAudioPlayer). */
async function prepareTtsUrl(text: string): Promise<string> {
  // 2026-09-04 (Martin: „in Mein Alltag hört man noch die alte Stimme"):
  // Hier stand `st.voiceId` — der Alltag fragte als einziger Weg nicht nach
  // der VORLESE-Stimme, sondern nahm die Grundstimme. Wer im Vorlese-Wähler
  // etwas anderes gewählt hatte, hörte es überall, nur hier nicht.
  const voiceId = vorleseStimme();
  const r = await fetch("/api/tts/prepare", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    // 2026-07-25 (Martin: "er soll die Information in der Datenbank speichern
    // und von dort auch rausnehmen"): KEIN voiceSource mehr. Der Vermerk
    // "bewusst gewaehlt" laesst den Server die mitgeschickte Stimme gewinnen —
    // gedacht fuer den Download-Waehler. Beim normalen Vorlesen schickte der
    // Client damit seine zwischengespeicherte Stimme, und ein veralteter
    // Browser-Stand schlug die Datenbank. Die voiceId geht weiterhin mit, sie
    // dient dem Server nur noch als Rueckfallwert.
    body: JSON.stringify({ text, voiceId }),
  });
  const j = (await r.json().catch(() => null)) as {
    ok?: boolean;
    token?: string;
  } | null;
  if (!r.ok || !j?.ok || !j.token) throw new Error("tts prepare failed");
  return `/api/tts/stream?token=${encodeURIComponent(j.token)}`;
}


// ---------------------------------------------------------------------------
// Aufnahme-Steuerung (nur native Hülle — Browser/PWA können nicht hintergrund-
// aufnehmen, dafür ist das native PawbotRecorder-Plugin da).
// ---------------------------------------------------------------------------


/** "06:49\u201306:49" -> "06:49" — identische Von-Bis-Spannen (Altbestand
 *  vor pre.1812) beim Rendern zusammenziehen. */
function zeitSpanneKompakt(text: string): string {
  return text.replace(/(\b\d{1,2}:\d{2})[\u2013-]\1\b/g, "$1");
}

/**
 * Der Griff beider Pause-Knöpfe (Karte und Aufnahme-Blatt).
 *
 * Der Knopf sagt, was Martin wollte (halte-pause-knopf.ts): Pausieren nur
 * nach Halten, Fortsetzen per Tipp. Genau das geschieht — auch wenn die
 * Anzeige hinter dem echten Zustand herhinkt (Ticket 2708, Nachbesserung).
 * Das Fortsetzen räumt fremde Pausengründe (05.09., „kann nach Pause nicht
 * mehr auf weiterklicken").
 */
function pauseGriff(absicht: PauseModus): void {
  void raeumeTonFuerAufnahme().then(() =>
    absicht === "pause" ? pauseRecorderForUser() : resumeRecorderForUser(),
  );
}

function buildRecorderControls(): HTMLElement | null {
  /*
   * ⚠️ 22.09.2026 — HIER STAND isRecorderAvailable().
   *    Damit gab es im Browser ueberhaupt keinen Aufnahme-Knopf, obwohl
   *    die Aufzeichnung dort seit dem 17.09. laeuft. Martin hat ihn
   *    gesucht und nicht gefunden, weil er nicht da war.
   */
  if (!kannAufzeichnen()) return null;

  const box = document.createElement("div");
  box.className = "alltag-recorder";

  const mainBtn = document.createElement("button");
  mainBtn.type = "button";
  mainBtn.className = "alltag-rec-btn";

  const pause = baueHaltePauseKnopf("alltag-rec-pause", pauseGriff);
  const pauseBtn = pause.knopf;
  pauseBtn.hidden = true;

  const stat = document.createElement("span");
  stat.className = "alltag-rec-status";
  stat.setAttribute("role", "status");
  stat.setAttribute("aria-live", "polite");

  const applyState = (s: RecorderState): void => {
    box.classList.toggle("is-recording", s === "recording" || s === "paused");
    mainBtn.disabled = s === "finishing";
    pauseBtn.hidden = !(s === "recording" || s === "paused");
    if (s === "idle") {
      mainBtn.textContent = "Alltag aufnehmen";
      stat.textContent = "";
    } else if (s === "recording") {
      mainBtn.textContent = "Stopp";
      pause.zeige("pause");
      // 2026-07-24 (Martin): kein Timer hier — der laeuft schon oben im
      // Topbar; zweimal dieselbe Uhrzeit sah komisch aus.
      stat.textContent = "";
    } else if (s === "paused") {
      mainBtn.textContent = "Stopp";
      pause.zeige("fortsetzen");
      stat.textContent = "";
    } else if (s === "finishing") {
      mainBtn.textContent = "Speichere";
      stat.textContent = "Wird hochgeladen";
      markRecordingProcessing();
    } else if (s === "error") {
      mainBtn.textContent = "Alltag aufnehmen";
      stat.textContent = "Fehler — erneut versuchen";
      // 2026-07-12 (Martin, "immer Fehler — weiss nicht ob gespeichert"):
      // der native error-Zustand ist oft aelter als die Wahrheit — die
      // Upload-Queue speichert im Hintergrund fertig. Deshalb den Server
      // fragen und die ehrliche Antwort zeigen statt des pauschalen Fehlers.
      void fetch("/api/recorder/session-status", { credentials: "include" })
        .then((r) => (r.ok ? (r.json() as Promise<unknown>) : null))
        .then((raw) => {
          if (getRecorderState() !== "error") return;
          const st = raw as {
            state?: string;
            doneStatus?: string;
            chunks?: number;
          } | null;
          if (!st?.state) return;
          if (st.state === "finished" && st.doneStatus === "entry") {
            stat.textContent = "Letzte Aufnahme gespeichert";
          } else if (st.state === "finished" && st.doneStatus === "discarded") {
            stat.textContent = "Letzte Aufnahme zu kurz — verworfen";
          } else if (st.state !== "finished" && (st.chunks ?? 0) > 0) {
            stat.textContent = "Übertragung läuft nach";
          }
        })
        .catch(() => {});
    }
  };

  const cb = {
    onState: applyState,
    onError: (err: Error) => {
      stat.textContent = `Fehler: ${err.message}`;
    },
  };

  mainBtn.addEventListener("click", () => {
    if (box.classList.contains("is-recording")) {
      void (async () => {
        if (await stopRecordingForUser()) hinweisAufnahmeHaelt();
      })();
    } else {
      // Gegenstueck zu Stop: Wollte Martin durchgehend aufnehmen (Schalter in
      // den Einstellungen), setzt der Start die Absicht wieder in Kraft — er
      // muss dafuer nicht in die Einstellungen. Erst starten, dann die
      // Absicht setzen: Die native Seite startet bei setAlwaysRecord(true)
      // nur, wenn nichts laeuft — nach dem Start laeuft etwas, also gibt es
      // keinen zweiten Anlauf.
      void (async () => {
        await startRecording(cb);
        try {
          const { loadSettings } = await import("./settings.js");
          if (loadSettings().alwaysRecord === true) {
            await setAlwaysDayRecording(true);
          }
        } catch {
          /* Einstellungen nicht lesbar — dann bleibt es bei dieser Sitzung. */
        }
      })();
    }
  });
  // 2026-07-20 (Martin, "Alltag aufnehmen trotz laufender Aufnahme"): die
  // Karte startete stur mit "idle" und erfuhr Wechsel nur ueber den eigenen
  // Knopf. Laeuft die Aufnahme nativ (Auto-Fortsetzung nach App-Start,
  // Overlay, Widget), blieb sie blind -- Widerspruch zum Header-Mikrofon.
  // Jetzt: echten Zustand uebernehmen, window-Event abonnieren (raeumt sich
  // selbst auf, sobald die Karte aus dem DOM fliegt) und den nativen
  // Recorder frisch befragen.
  applyState(getRecorderState());
  let boxWasConnected = false;
  const onGlobalState = (ev: Event): void => {
    if (box.isConnected) {
      boxWasConnected = true;
    } else if (boxWasConnected) {
      window.removeEventListener("voice-v8:recorder-state", onGlobalState);
      return;
    }
    const s = (ev as CustomEvent<{ state?: RecorderState }>).detail?.state;
    if (s) applyState(s);
  };
  window.addEventListener("voice-v8:recorder-state", onGlobalState);
  void syncFromNative();
  box.appendChild(mainBtn);
  box.appendChild(pauseBtn);
  box.appendChild(stat);
  return box;
}

// ---------------------------------------------------------------------------
// Sheet-Aufbau
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Orte-Reiter (Phase E, 2026-07-13, Martin: "Karte, wo ich mich am meisten
// aufhalte"). Rangliste der meistbesuchten Orte nach Verweildauer. Vorwaerts-
// wirksam — ehrlicher Leer-Zustand, solange keine Aufnahme GPS hat.
// ---------------------------------------------------------------------------
let activeTab: "tage" | "orte" | "sprecher" = "tage";
// 2026-07-14 (Martin, "Tabs schalten zeitverzoegert"): einmal gerenderte
// Reiter bleiben im DOM stehen und werden beim Umschalten SOFORT gezeigt;
// neu geladen wird nur, wenn die Daten aelter als 5 min sind (oder nach
// einer Aenderung — Mutations-Fluesse rufen renderSpeakers direkt auf und
// erneuern damit den Stand ohnehin).
const TAB_CACHE_MS = 5 * 60_000;
let placesLoadedAt = 0;
let speakersLoadedAt = 0;
/** Der Schlüssel der Stimme, nach der die Glocke fragt, solange die Seite so geöffnet wurde („Benennen“). */
let glockenFrage: string | null = null;

function buildAlltagTabs(): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "alltag-tabs";
  bar.setAttribute("role", "tablist");
  const mk = (
    tab: "tage" | "orte" | "sprecher",
    label: string,
  ): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "alltag-tab" + (activeTab === tab ? " is-active" : "");
    b.dataset.tab = tab;
    b.textContent = label;
    b.setAttribute("role", "tab");
    b.setAttribute("aria-selected", activeTab === tab ? "true" : "false");
    b.addEventListener("click", () => switchTab(tab));
    return b;
  };
  bar.appendChild(mk("tage", "Tage"));
  bar.appendChild(mk("orte", "Orte"));
  // 2026-07-21 (Martins Freigabe): "Sprecher" ist Verwaltung, kein
  // Inhalt — wohnt jetzt im ⋯-Menue des Kopfes.
  return bar;
}

function switchTab(tab: "tage" | "orte" | "sprecher"): void {
  activeTab = tab;
  const el = sheetEl;
  if (!el) return;
  const list = el.querySelector<HTMLElement>(".alltag-list");
  const places = el.querySelector<HTMLElement>(".alltag-places");
  const speakers = el.querySelector<HTMLElement>(".alltag-speaker-mgmt");
  const status = el.querySelector<HTMLElement>(".material-status");
  if (list) list.hidden = tab !== "tage";
  if (places) places.hidden = tab !== "orte";
  if (speakers) speakers.hidden = tab !== "sprecher";
  // Die Lade-/Fehlerzeile gehoert zur Tages-Liste.
  if (status) status.hidden = tab !== "tage";
  // Aufnahme gehoert zum Tage-Blick. NUR hidden-Toggle — das Element
  // bleibt im DOM, die laufende native Aufnahme wird nicht angefasst.
  const rec = el.querySelector<HTMLElement>(".alltag-recorder");
  if (rec) rec.hidden = tab !== "tage";
  el.querySelectorAll<HTMLElement>(".alltag-tab").forEach((b) => {
    const on = b.dataset.tab === tab;
    b.classList.toggle("is-active", on);
    b.setAttribute("aria-selected", on ? "true" : "false");
  });
  // Frisch = Inhalt steht im DOM (auch ein Leer-Hinweis zaehlt) UND das
  // letzte erfolgreiche Laden ist juenger als TAB_CACHE_MS. Bei neu
  // aufgebautem Sheet ist die Box leer -> es wird geladen.
  if (tab === "orte") {
    const fresh =
      places?.hasChildNodes() && Date.now() - placesLoadedAt < TAB_CACHE_MS;
    if (!fresh) void renderPlaces(el);
  }
  if (tab === "sprecher") {
    const fresh =
      speakers?.hasChildNodes() &&
      Date.now() - speakersLoadedAt < TAB_CACHE_MS;
    if (!fresh) void renderSpeakers(el);
  }
}

async function renderPlaces(el: HTMLElement): Promise<void> {
  const box = el.querySelector<HTMLElement>(".alltag-places");
  if (!box) return;
  box.replaceChildren();
  // 2026-07-20 (Martin, "nicht linksbuendig in der Ecke"): zentrierter
  // Spinner statt nackter Textzeile. Fehlerfall ueberschreibt den Inhalt
  // mit der Meldung (Spinner verschwindet dann).
  const loading = buildLoadingHint("Wird geladen …");
  box.appendChild(loading);

  let data: Awaited<ReturnType<typeof fetchAlltagPlaces>>;
  try {
    data = await fetchAlltagPlaces();
  } catch (err) {
    loading.textContent = `Fehler: ${(err as Error).message}`;
    return;
  }
  placesLoadedAt = Date.now();
  box.replaceChildren();

  const meta = data.meta;
  // Ehrlicher Leer-Zustand: noch keine verorteten Aufnahmen -> kein leerer
  // Kartenrahmen, sondern ein klarer Hinweis (nichts wirkt wie ein Bug).
  if (!meta || meta.withLocation === 0 || data.places.length === 0) {
    const empty = document.createElement("div");
    empty.className = "alltag-places-empty";
    const h = document.createElement("p");
    h.className = "alltag-places-empty-title";
    h.textContent = "Noch keine Orte erfasst";
    const p = document.createElement("p");
    p.className = "alltag-places-empty-sub";
    p.textContent = meta
      ? `Die Standort-Erfassung ist neu — Orte erscheinen mit neuen Aufnahmen. Bisher ${meta.withLocation} von ${meta.totalEntries} Aufnahmen mit Standort.`
      : "Noch keine Alltags-Aufnahmen.";
    empty.appendChild(h);
    empty.appendChild(p);
    box.appendChild(empty);
    return;
  }

  // Kopf: wie viele Aufnahmen verortet (Ehrlichkeit ueber die Datenlage).
  const head = document.createElement("p");
  head.className = "alltag-places-meta";
  const orte = data.places.length === 1 ? "1 Ort" : `${data.places.length} Orte`;
  head.textContent = `${meta.withLocation} von ${meta.totalEntries} Aufnahmen verortet · ${orte}`;
  box.appendChild(head);

  // Karte (serverseitiges OSM-PNG, same-origin -> CSP-immun). Bei 204/Render-
  // Fehler (offline/keine Orte) still ausblenden -> nur die Rangliste bleibt.
  const img = document.createElement("img");
  img.className = "alltag-places-map";
  img.alt = "Karte deiner Orte";
  img.loading = "lazy";
  img.src = "/api/alltag/places-map.png";
  img.addEventListener("error", () => {
    img.hidden = true;
  });
  box.appendChild(img);

  // Tap auf eine Zeile -> Karte auf diesen Ort zentrieren (?focus=).
  const focusOn = (id: string): void => {
    img.hidden = false;
    img.src = `/api/alltag/places-map.png?focus=${encodeURIComponent(id)}`;
  };

  const listEl = document.createElement("div");
  listEl.className = "alltag-places-list";
  data.places.forEach((pl, i) =>
    listEl.appendChild(buildPlaceRow(pl, i + 1, focusOn)),
  );
  box.appendChild(listEl);
}

function buildPlaceRow(
  pl: PlaceCluster,
  rank: number,
  onFocus?: (id: string) => void,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "alltag-place-row";
  if (onFocus) {
    row.setAttribute("role", "button");
    row.tabIndex = 0;
    row.addEventListener("click", () => onFocus(pl.id));
    row.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onFocus(pl.id);
      }
    });
  }

  const rankEl = document.createElement("span");
  rankEl.className = "alltag-place-rank";
  rankEl.textContent = String(rank);
  row.appendChild(rankEl);

  const main = document.createElement("div");
  main.className = "alltag-place-main";
  const label = document.createElement("div");
  label.className = "alltag-place-label";
  label.textContent = pl.label;
  main.appendChild(label);
  const sub = document.createElement("div");
  sub.className = "alltag-place-sub";
  const visits = pl.visitCount === 1 ? "1 Besuch" : `${pl.visitCount} Besuche`;
  const days = pl.dayCount === 1 ? "1 Tag" : `${pl.dayCount} Tage`;
  sub.textContent = `${visits} · ${days}`;
  main.appendChild(sub);
  row.appendChild(main);

  const time = document.createElement("div");
  time.className = "alltag-place-time";
  // Verweildauer wiederverwendet den Zeit-Formatter der Still-Zeitachse.
  time.textContent = pl.totalMinutes > 0 ? formatGap(pl.totalMinutes * 60000) : "—";
  row.appendChild(time);

  return row;
}

// ---------------------------------------------------------------------------
// Sprecher-Reiter (Phase D, 2026-07-13, Martin: neue Personen wie Peta
// verwalten). Register listen/umbenennen/entfernen. "Du" ist geschuetzt.
// Erfasst werden Sprecher weiterhin ueber den Benenn-Klick in einer Aufnahme
// (dort wird der Stimmabdruck aufgenommen); hier verwaltet man das Ergebnis.
// ---------------------------------------------------------------------------
async function renderSpeakers(el: HTMLElement): Promise<void> {
  const box = el.querySelector<HTMLElement>(".alltag-speaker-mgmt");
  if (!box) return;
  box.replaceChildren();
  // 2026-07-20 (Martin, "nicht linksbuendig in der Ecke"): zentrierter
  // Spinner statt nackter Textzeile. Fehlerfall ueberschreibt den Inhalt
  // mit der Meldung (Spinner verschwindet dann).
  const loading = buildLoadingHint("Wird geladen …");
  box.appendChild(loading);

  let data: Awaited<ReturnType<typeof fetchSpeakers>>;
  try {
    data = await fetchSpeakers();
  } catch (err) {
    loading.textContent = `Fehler: ${(err as Error).message}`;
    return;
  }
  speakersLoadedAt = Date.now();
  box.replaceChildren();

  const hint = document.createElement("p");
  hint.className = "alltag-spk-hint";
  hint.textContent =
    "Bekannte Stimmen. Eine neue Person erfasst du, indem du in einer Aufnahme einen erkannten Sprecher benennst — sie erscheint dann hier und wird künftig automatisch wiedererkannt.";
  box.appendChild(hint);

  if (data.speakers.length === 0) {
    const empty = document.createElement("p");
    empty.className = "alltag-spk-empty";
    empty.textContent = "Noch keine benannten Stimmen.";
    box.appendChild(empty);
  } else {
    const listEl = document.createElement("div");
    listEl.className = "alltag-spk-list";
    for (const sp of data.speakers) listEl.appendChild(buildSpeakerRow(el, sp));
    box.appendChild(listEl);
  }

  /*
   * ⚠️ 20.09.2026 (Ticket 2391) — HIER STAND EIN `return`.
   *
   *    Bei null bekannten Stimmen brach die Funktion ab. Genau dann ist die
   *    Liste der UNBENANNTEN aber am wichtigsten: 400 Aufnahmen lang hat
   *    niemand jemanden benannt, also ist „noch keine benannten Stimmen"
   *    der Normalfall, nicht die Ausnahme.
   */
  void renderOffeneStimmen(box, () => void renderSpeakers(el));
}


/**
 * Eine Zeile der Liste „noch nicht benannt". (Ticket 2391, 20.09.2026)
 *
 * ⚠️ Ausgelagert und exportiert, damit sie pruefbar ist. Der umgebende
 *    Bereich haengt an `fetch` und laesst sich nicht sinnvoll aufrufen; die
 *    Zeile selbst ist die Sache, um die es geht.
 *
 * Was sie zeigt, ist aus den Daten gewaehlt, nicht geraten: die lauteste
 * unbenannte Stimme im Bestand hat 597 Minuten an 22 Tagen geredet. Ohne
 * „an wie vielen Tagen" sieht ein einmaliger Gast genauso aus wie jemand,
 * der jede Woche da ist.
 */
export function buildOffeneStimmeRow(
  s: OffeneStimmeZeile,
  benennen: (name: string) => void,
): HTMLElement {
  /*
   * ⚠️ 20.09.2026 — HIER STAND `.alltag-spk-row`, UND DAS HAT NICHT GEPASST.
   *
   *    Martin, mit Bild: *„die Darstellung von den Sprechern an. Auswahlen
   *    passt ueberhaupt nicht. ... Wie kann ich jetzt Namen zuweisen?"*
   *
   *    Die geteilte Zeile ist fuer [Name][Kuerzel][zwei kleine Knoepfe]
   *    gebaut: der Name traegt `flex: 1 1 auto` mit `overflow: hidden`, die
   *    Knoepfe `flex: 0 0 auto`. Mein Inhalt ist aber
   *    [Zahl][Meta][Eingabefeld + Knopf] — und ein Eingabefeld will Platz.
   *    Auf dem Telefon drueckte es die Minuten auf null Breite (sie
   *    verschwanden ganz) und schob den Speichern-Knopf aus dem Bild. Uebrig
   *    blieb ein Feld ohne erkennbaren Zweck.
   *
   *    Wiederverwenden ist richtig — aber nur, wo die Form auch passt. Hier
   *    passt sie nicht, also bekommt diese Zeile ihre eigene: zwei Zeilen
   *    statt einer gequetschten.
   */
  const row = document.createElement("div");
  row.className = "alltag-offen-row";

  const kopf = document.createElement("div");
  kopf.className = "alltag-offen-kopf";

  const wer = document.createElement("span");
  wer.className = "alltag-offen-zeit";
  wer.textContent = sprechdauer(s.sekunden);
  kopf.appendChild(wer);
  if (s.frage) {
    // Nach dieser Stimme hat die Glocke gefragt — sie steht darum oben, auch wenn sie länger nicht zu hören war.
    row.classList.add("alltag-offen-row--frage");
    const marke = document.createElement("span");
    marke.className = "alltag-offen-frage";
    marke.textContent = "Danach hat Vespera gefragt";
    kopf.appendChild(marke);
  }

  const meta = document.createElement("span");
  meta.className = "alltag-offen-meta";
  const tage = s.tage === 1 ? "an einem Tag" : `an ${s.tage} Tagen`;
  meta.textContent = `${tage} · zuletzt ${s.zuletzt.slice(8, 10)}.${s.zuletzt.slice(5, 7)}.`;
  kopf.appendChild(meta);
  row.appendChild(kopf);

  const zeile = document.createElement("div");
  zeile.className = "alltag-offen-eingabe";
  const input = document.createElement("input");
  input.type = "text";
  input.className = "alltag-spk-input";
  input.placeholder = "Wer ist das?";
  input.maxLength = 40;
  input.setAttribute("aria-label", `Name für die Stimme mit ${sprechdauer(s.sekunden)}`);
  const speichern = (): void => {
    const nm = input.value.trim();
    if (!nm) return;
    input.disabled = true;
    btn.disabled = true;
    benennen(nm);
  };
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      speichern();
    }
  });
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "alltag-spk-btn alltag-offen-btn";
  btn.textContent = "Speichern";
  btn.addEventListener("click", speichern);
  zeile.append(input, btn);
  row.appendChild(zeile);
  return row;
}

/**
 * Der Block „noch nicht benannt" unter den bekannten Stimmen.
 *
 * ⚠️ Er laedt NUR, wenn dieser Bereich offen ist — und meldet sich nie von
 *    selbst. Martin hat die „Unbekannte Stimme"-Glocke am 24.07.2026
 *    abbestellt, weil sie bei Ganztags-Aufnahmen zu Laerm wurde. Eine Liste
 *    zum Nachsehen ist das Gegenteil davon.
 */
async function renderOffeneStimmen(
  box: HTMLElement,
  neuLaden: () => void,
): Promise<void> {
  let daten: Awaited<ReturnType<typeof fetchOffeneStimmen>>;
  try {
    daten = await fetchOffeneStimmen(glockenFrage);
  } catch {
    return; // Der Bereich bleibt ohne diesen Block brauchbar.
  }
  if (daten.stimmen.length === 0) return;

  const titel = document.createElement("p");
  titel.className = "alltag-spk-hint";
  titel.textContent =
    "Noch nicht benannt. Wer hier einen Namen bekommt, wird künftig automatisch wiedererkannt.";
  box.appendChild(titel);

  const liste = document.createElement("div");
  liste.className = "alltag-spk-list";
  for (const s of daten.stimmen) {
    liste.appendChild(
      buildOffeneStimmeRow(s, (name) => {
        void nameSpeaker(s.besteAufnahme.aufnahmeId, s.besteAufnahme.label, name)
          .then(() => {
            // Die Frage in der Glocke ist beantwortet: der Punkt darf nicht veraltet stehen bleiben.
            frageGlockeNeu();
            neuLaden();
          })
          .catch(() => neuLaden());
      }),
    );
  }
  box.appendChild(liste);

  /*
   * ⚠️ Die Liste ist ABGESCHNITTEN, und das gehoert dazugesagt. Im Bestand
   *    liegen 258 zusammengelegte Stimmen; gezeigt werden die lautesten
   *    zwoelf. Ohne diese Zeile sieht die Liste vollstaendig aus.
   */
  if (daten.stimmenGesamt > daten.stimmen.length) {
    const rest = document.createElement("p");
    rest.className = "alltag-spk-empty";
    rest.textContent = `Die lautesten ${daten.stimmen.length} von ${daten.stimmenGesamt} — der Rest sind kurze Auftritte.`;
    box.appendChild(rest);
  }
}

function buildSpeakerRow(el: HTMLElement, sp: Speaker): HTMLElement {
  const row = document.createElement("div");
  row.className = "alltag-spk-row";

  const nameEl = document.createElement("span");
  nameEl.className = "alltag-spk-name";
  nameEl.textContent = sp.name;
  row.appendChild(nameEl);

  if (sp.isMe) {
    const lock = document.createElement("span");
    lock.className = "alltag-spk-lock";
    lock.textContent = "du · geschützt";
    row.appendChild(lock);
    return row;
  }

  const actions = document.createElement("div");
  actions.className = "alltag-spk-actions";

  const renameBtn = document.createElement("button");
  renameBtn.type = "button";
  renameBtn.className = "alltag-spk-btn";
  renameBtn.textContent = "Umbenennen";
  renameBtn.addEventListener("click", () => startSpeakerRename(el, row, sp));

  const delBtn = document.createElement("button");
  delBtn.type = "button";
  delBtn.className = "alltag-spk-btn is-danger";
  delBtn.textContent = "Entfernen";
  delBtn.addEventListener("click", () => {
    void (async () => {
      const ok = await confirmSheet({
        title: `${sp.name} entfernen?`,
        message:
          "Der Stimmabdruck wird gelöscht. Künftige Aufnahmen erkennen diese Person nicht mehr automatisch. Bestehende Einträge bleiben unverändert.",
        confirmLabel: "Entfernen",
        danger: true,
      });
      if (!ok) return;
      try {
        await deleteSpeaker(sp.name);
      } catch {
        /* Fehler -> Re-Render zeigt den unveraenderten Stand */
      }
      await renderSpeakers(el);
    })();
  });

  actions.appendChild(renameBtn);
  actions.appendChild(delBtn);
  row.appendChild(actions);
  return row;
}

function startSpeakerRename(
  el: HTMLElement,
  row: HTMLElement,
  sp: Speaker,
): void {
  row.replaceChildren();
  const input = document.createElement("input");
  input.type = "text";
  input.className = "alltag-spk-input";
  input.value = sp.name;
  input.maxLength = 40;

  const save = document.createElement("button");
  save.type = "button";
  save.className = "alltag-spk-btn";
  save.textContent = "Speichern";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "alltag-spk-btn";
  cancel.textContent = "Abbrechen";

  const doSave = (): void => {
    const to = input.value.trim();
    if (!to || to === sp.name) {
      void renderSpeakers(el);
      return;
    }
    void (async () => {
      try {
        await renameSpeaker(sp.name, to);
      } catch {
        /* Fehler -> Re-Render zeigt den alten Namen */
      }
      await renderSpeakers(el);
    })();
  };

  save.addEventListener("click", doSave);
  cancel.addEventListener("click", () => void renderSpeakers(el));
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      doSave();
    } else if (e.key === "Escape") {
      void renderSpeakers(el);
    }
  });

  row.appendChild(input);
  row.appendChild(save);
  row.appendChild(cancel);
  input.focus();
  input.select();
}

function buildSheet(): HTMLElement {
  const el = document.createElement("div");
  el.className = "sheet material-sheet page-sheet";
  el.id = "alltag-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.hidden = true;

  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  backdrop.addEventListener("click", closeAlltagSheet);
  el.appendChild(backdrop);

  const card = document.createElement("div");
  card.className = "sheet-card material-card";

  // Einheitlicher Kopf mit ⋯-Menue (Sprecher = Verwaltung, kein Tab).
  const header = buildPageHeader("Mein Alltag", closeAlltagSheet, {
    menuItems: [
      { label: "Sprecher verwalten", onSelect: () => switchTab("sprecher") },
    ],
  });
  card.appendChild(header);

  // NORM Regel 3: Inhalt in der geteilten Lesespalte (Kopf bleibt vollbreit).
  const pageBody = document.createElement("div");
  pageBody.className = "page-body";
  card.appendChild(pageBody);

  // Reiter „Tage | Orte" (Phase E): Tages-Liste vs Orts-Rangliste.
  pageBody.appendChild(buildAlltagTabs());

  // Aufnahme-Knopf (nur native Hülle) — gehoert zum Tab "Tage".
  const recorder = buildRecorderControls();
  if (recorder) pageBody.appendChild(recorder);

  const status = document.createElement("p");
  status.className = "material-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  pageBody.appendChild(status);

  const list = document.createElement("div");
  list.className = "alltag-list";
  pageBody.appendChild(list);

  // Orts-Rangliste (Phase E) — eigener Container, per Reiter umgeschaltet.
  const places = document.createElement("div");
  places.className = "alltag-places";
  places.hidden = true;
  pageBody.appendChild(places);

  // Sprecher-Verwaltung (Phase D) — eigener Container, per Reiter.
  const speakerMgmt = document.createElement("div");
  speakerMgmt.className = "alltag-speaker-mgmt";
  speakerMgmt.hidden = true;
  pageBody.appendChild(speakerMgmt);

  el.appendChild(card);
  document.body.appendChild(el);
  /* 2026-08-30: Der eigene Aufruf ist entfallen — buildPageHeader meldet
     jede Seite mit geteiltem Kopf beim einen Wisch-Waechter an. Zwei
     Handler haetten denselben Rueckweg zweimal ausgeloest. */
  return el;
}

// ---------------------------------------------------------------------------
// Rendern
// ---------------------------------------------------------------------------

/**
 * Leitet aus den Alltag-Tagesdaten die kompakte Widget-Info ab (letzte Aufnahme
 * heute, Uhrzeit, Anzahl) und schiebt sie ans Home-Screen-Widget. Fire-and-forget;
 * im Browser/alter Hülle stiller No-op. Darf nie werfen.
 */
function pushWidgetInfoFromData(
  data: Awaited<ReturnType<typeof fetchAlltag>>,
): void {
  try {
    const today = new Date().toLocaleDateString("en-CA", {
      timeZone: "Europe/Vienna",
    });
    const day = data.days.find((d) => d.dayKey === today);
    const countToday = day?.entries.length ?? 0;
    // „Zuletzt" = neueste Aufnahme ÜBERHAUPT (wie Audible: zeigt dein aktuelles
    // Hörbuch, egal wann zuletzt gehört) — nicht nur heute. days[0] ist der
    // jüngste Tag (newest-first), dessen entries[0] die jüngste Aufnahme.
    pushWidgetInfo(data.days[0]?.entries[0]?.title ?? "", countToday);
  } catch {
    /* Widget-Update darf nichts kippen */
  }
}

/**
 * 2026-07-18 (Martin): Widget-Felder aus dem TITEL des juengsten Eintrags
 * bauen. WARUM ausgelagert: die schlanke Status-Route liefert genau diesen
 * Titel — so teilen volle Liste und Status EINE Formatierung (kein Drift).
 */
function pushWidgetInfo(rawTitle: string, countToday: number): void {
  const lastTitle =
    rawTitle.replace(/^Alltag\s+\d{2}\.\d{2}\.\d{4},?\s*/, "").trim() ||
    rawTitle;
  const lastTimeHHMM = extractTimeFromTitle(rawTitle) ?? "";
  void pushAlltagWidgetInfo({ lastTitle, lastTimeHHMM, countToday });
}

/**
 * Widget-Info aktualisieren OHNE die Ansicht zu öffnen — für App-Start/Vordergrund,
 * damit das Home-Screen-Widget frische Daten zeigt, auch wenn „Mein Alltag" nicht
 * geöffnet wurde. Nur in der nativen Hülle (sonst unnötiger Fetch). Wirft nie.
 */
export async function refreshAlltagWidget(): Promise<void> {
  if (!isRecorderAvailable()) return;
  // 2026-07-18 (Martin, Verschwendung): gebraucht werden nur Titel/Uhrzeit des
  // juengsten Eintrags + die Zahl von heute. Vorher holte das bei App-Start UND
  // bei JEDEM Vordergrund-Wechsel die komplette Liste (~120 kB).
  // 2026-07-18 (Martin, Haertung): try/catch zurueck. WARUM: die Doku oben sagt
  // „Wirft nie" — beim Umbau ging der Schutzring verloren. Ein Fehler hier (Netz,
  // Bruecke zur nativen Huelle) darf den Aufrufer (App-Start/Vordergrund) nie
  // mitreissen; das Widget bleibt dann einfach beim alten Stand.
  try {
    const st = await fetchAlltagStatus(1);
    if (!st?.now) return;
    pushWidgetInfo(st.now.lastEntryTitle ?? "", st.now.todayEntries);
  } catch {
    /* Widget-Auffrischung ist reines Beiwerk — Fehler bleiben hier stehen. */
  }
}

// Sanftes Nachladen, solange Aufnahmen verarbeitet werden (Karte verschwindet,
// fertiger Eintrag erscheint). EIN Timer; beim Schließen/Neu-Rendern gelöscht.
let processingPoll: number | null = null;
function clearProcessingPoll(): void {
  if (processingPoll !== null) {
    clearTimeout(processingPoll);
    processingPoll = null;
  }
}

// Welche Tage aufgeklappt sind. Modul-State, damit der offene Zustand einen
// Neu-Aufbau (Datenänderung) übersteht und nicht bei jedem Poll zuklappt.
const openDayKeys = new Set<string>();
let lastEnsuredToday = "";

/** Heutiger Tagesschlüssel (YYYY-MM-DD, Europe/Vienna) — wie im Widget. */
function todayKey(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Vienna" });
}

/**
 * Sorgt dafür, dass der HEUTIGE Tag aufgeklappt ist. Wechselt der Tag (auch wenn
 * die App über Mitternacht offen bleibt), wird der alte „heute" zugeklappt und
 * der neue aufgeklappt (Martins Wunsch). Beim allerersten Mal zusätzlich den
 * jüngsten vorhandenen Tag öffnen, falls es heute noch nichts gibt.
 */
function ensureOpenForToday(days: AlltagDay[]): void {
  const today = todayKey();
  if (today === lastEnsuredToday) return;
  const firstRun = lastEnsuredToday === "";
  if (lastEnsuredToday) openDayKeys.delete(lastEnsuredToday);
  openDayKeys.add(today);
  if (firstRun && !days.some((d) => d.dayKey === today) && days[0]) {
    openDayKeys.add(days[0].dayKey);
  }
  lastEnsuredToday = today;
}

// Sofort-Rückmeldung „wird hochgeladen/verarbeitet": beim Stoppen gesetzt,
// zeigt SOFORT eine Karte. PERSISTENT in localStorage (übersteht Navigation
// UND WebView-Reload) — eine lange Aufnahme (ggf. verzögerter Upload im
// Flugmodus) braucht weit länger als die früheren 90 s. Bleibt, bis der Server
// sie selbst als `processing` meldet, ein Eintrag NACH dem Stopp erscheint,
// oder die Sicherheits-Frist abläuft.
const PROC_KEY = "alltag:processingSince";
const PROC_CAP_MS = 30 * 60 * 1000;
function markRecordingProcessing(): void {
  try {
    localStorage.setItem(PROC_KEY, String(Date.now()));
  } catch {
    /* localStorage nicht verfügbar */
  }
}
function clearRecordingProcessing(): void {
  try {
    localStorage.removeItem(PROC_KEY);
  } catch {
    /* egal */
  }
}
/** Stopp-Zeit (ms), solange die Aufnahme in der Pipeline ist (innerhalb der
 *  Sicherheits-Frist); sonst null. */
function processingSince(): number | null {
  try {
    const raw = localStorage.getItem(PROC_KEY);
    if (!raw) return null;
    const since = parseInt(raw, 10);
    if (!Number.isFinite(since) || Date.now() - since > PROC_CAP_MS) {
      clearRecordingProcessing();
      return null;
    }
    return since;
  } catch {
    return null;
  }
}
/** Upload-Zustand fürs Karten-Label (best effort; nur nativ). */
function describeUpload(): { uploading: boolean; pending: number } {
  try {
    if (!isRecorderAvailable()) return { uploading: false, pending: 0 };
    const info = getUploadInfo();
    return {
      uploading: getRecorderState() === "finishing" || info.pending > 0,
      pending: info.pending,
    };
  } catch {
    return { uploading: false, pending: 0 };
  }
}

/**
 * Signatur der Daten (Verarbeitungs-IDs + Tage/Einträge/Rollup-Länge). Ändert
 * sie sich beim stillen Poll nicht, wird NICHT neu gezeichnet — so bleiben
 * offene Tage, ein laufender Player und die Scroll-Position erhalten.
 */
function alltagSignature(
  data: Awaited<ReturnType<typeof fetchAlltag>>,
): string {
  const proc = (data.processing ?? []).map((p) => p.id).join(",");
  // 2026-07-18 (Martin): auch die „unverarbeitet"-Karten in die Signatur —
  // sonst zeichnet der stille Poll sie nie neu. Die Poll-BEDINGUNG bleibt
  // bewusst unberuehrt, sonst pollt die Ansicht wegen Altlasten dauerhaft.
  const unproc = (data.unprocessed ?? []).map((u) => u.id).join(",");
  const days = data.days
    .map(
      (d) =>
        `${d.dayKey}:${d.rollup ? d.rollup.length : 0}:${d.entries
          // ID UND Inhaltsmaße (summary/sections/todos), damit ein Re-Digest
          // (gleiche ID, geänderter Text) den stillen Poll neu zeichnen lässt.
          .map(
            (e) =>
              `${e.id}.${e.summary.length}.${e.sections.length}.${e.todos.length}`,
          )
          .join("|")}`,
    )
    .join(";");
  return `${proc}#${unproc}##${days}`;
}
let lastSignature = "";

/** Vergleichs-Signatur der schlanken Status-Sicht. */
function nowSignature(n: AlltagNow): string {
  return `${n.processing}|${n.lastEntryAt ?? ""}|${n.todayEntries}`;
}
let lastNowSignature = "";

// 2026-07-18 (Martin, Haertung): Abbruch nach mehreren Fehlschlaegen IN FOLGE.
// WARUM: der stille Poll plante sich im Fehlerfall IMMER neu ein — bei einem
// dauerhaft nicht erreichbaren Server drehte er sich alle 12 s endlos weiter,
// solange das Sheet offen ist, ohne sich selbst befreien zu koennen. Ein
// einzelner Fehlschlag wird weiterhin normal wiederholt; erst ab POLL_FAIL_MAX
// gibt der Poll auf. Jeder gelungene Versuch setzt den Zaehler zurueck; der
// visibilitychange-Refresh startet die Beobachtung ohnehin neu.
const POLL_FAIL_MAX = 5;
let pollFailStreak = 0;

/**
 * Beobachtungs-Poll (12 s) — NUR solange wirklich etwas laeuft.
 *
 * 2026-07-18 (Martin, Verschwendung): vorher lief IMMER ein Timer (12 s aktiv,
 * 30 s im Leerlauf) und holte jedes Mal die KOMPLETTE Liste (~120 kB, ~180
 * Notiz-Dateien am Server) — auch wenn gar nichts passierte. Jetzt gilt:
 *  - kein Vorgang  → gar kein Timer,
 *  - Vorgang       → billige Status-Abfrage (~120 Byte); die volle Liste wird
 *                    erst geholt, wenn sich am Status wirklich etwas aendert.
 * Neue Eintraege von anderen Geraeten holt weiterhin der
 * visibilitychange-Refresh (wireAlltagVisibilityRefresh). Die
 * „unverarbeitet"-Karten loesen bewusst NICHTS aus (Dauerzustand, kein Lauf).
 */
function schedulePoll(el: HTMLElement, active: boolean): void {
  clearProcessingPoll();
  if (!active) return;
  processingPoll = window.setTimeout(() => {
    processingPoll = null;
    void (async () => {
      if (!sheetEl || sheetEl.hidden) return;
      // Verdeckte App (Tagesaufnahme im Hintergrund): nicht abfragen, nicht bauen. Die Rückkehr
      // (wireAlltagVisibilityRefresh) zeichnet still neu und plant den nächsten Blick (pre.3620).
      if (document.visibilityState !== "visible") return;
      const st = await fetchAlltagStatus(1);
      // Nicht erreichbar: das ist ein Fehlschlag — begrenzt weiter beobachten.
      if (!st?.now) {
        pollFailStreak += 1;
        if (pollFailStreak < POLL_FAIL_MAX) schedulePoll(el, true);
        return;
      }
      pollFailStreak = 0;
      // Erreichbar, aber unveraendert: kein Fehler — unbegrenzt weiter beobachten.
      if (nowSignature(st.now) === lastNowSignature) {
        schedulePoll(el, true);
        return;
      }
      lastNowSignature = nowSignature(st.now);
      void renderList(el, { quiet: true });
    })();
  }, 12000);
}

// 2026-07-20 (Martin, "Ladezeichen nicht schoen positioniert"): EIN
// geteilter Lade-Indikator fuer alle Reiter — zentrierter Akzent-Spinner,
// der im freien Raum schwebt statt links oben zu kleben.
/* 2026-08-30: buildLoadIndicator ist nach sheet-ui.ts gewandert und heisst
   dort buildLoadingHint — EIN Bauer fuer die ganze App. */

async function renderList(
  el: HTMLElement,
  opts?: { quiet?: boolean },
): Promise<void> {
  clearProcessingPoll();
  const list = el.querySelector<HTMLElement>(".alltag-list")!;
  const status = el.querySelector<HTMLElement>(".material-status")!;
  const quiet = opts?.quiet ?? false;

  // Nur beim ersten/echten Laden „Lade …" zeigen und die Liste leeren. Beim
  // Hintergrund-Poll NICHT — sonst blitzt der Inhalt zyklisch weg.
  if (!quiet) {
    list.replaceChildren();
    // Ticket 2698 (Nachbesserung pre.3394): die Liste ist leer — ihr
    // neuester Player auch. Sonst spielte ein abgehängter Player von vorhin
    // los, wenn der Abruf gleich scheitert: Ton ohne sichtbaren Stopp.
    status.textContent = "";
    list.appendChild(buildLoadingHint("Dein Alltag wird geladen …"));
  }

  // 2026-07-18 (Martin, Verschwendung): den Tages-Status PARALLEL anstossen.
  // WARUM: er lief bisher erst NACH /api/alltag und schob das erste Malen um
  // einen kompletten Roundtrip nach hinten. loadDayStatus wirft nie.
  const dayStatusReady = loadDayStatus();

  let data: Awaited<ReturnType<typeof fetchAlltag>>;
  try {
    data = await fetchAlltag();
  } catch (err) {
    // Stiller Poll: vorhandenen Inhalt NICHT durch eine Fehlermeldung ersetzen,
    // einfach später erneut versuchen.
    if (!quiet) status.textContent = `Fehler: ${(err as Error).message}`;
    else {
      // 2026-07-18 (Martin): Signatur verwerfen. WARUM: der billige Poll laedt
      // die volle Liste nur bei einer AENDERUNG nach — nach einem Fehlschlag
      // muss der naechste Versuch aber in jedem Fall stattfinden. "" kann keine
      // echte Signatur sein, passt also garantiert nicht.
      lastNowSignature = "";
      // 2026-07-18 (Martin, Haertung): nur begrenzt oft nachfassen — sonst dreht
      // sich der Poll bei dauerhaft fehlerhaftem Server endlos im Kreis.
      pollFailStreak += 1;
      if (pollFailStreak < POLL_FAIL_MAX) schedulePoll(el, true);
    }
    return;
  }

  // Inzwischen geschlossen: nichts in die versteckte Seite bauen (pre.3621). `lastSignature` bleibt,
  // wie es war — das nächste Öffnen baut ohnehin neu (nicht still).
  if (el.hidden) return;
  // Abruf hat geklappt → Fehler-Serie zuruecksetzen (2026-07-18).
  pollFailStreak = 0;
  status.textContent = "";
  pushWidgetInfoFromData(data);
  if (data.projectId) alltagProjectId = data.projectId;
  // 2026-07-03 (Martin, #37): Verarbeitungs-Status (Luecken-Badges) — oben
  // parallel gestartet, hier nur noch einsammeln.
  await dayStatusReady;
  if (el.hidden) return;

  const processing = data.processing ?? [];

  // Persistente Sofort-Karte (localStorage): sichtbar, solange eine gestoppte
  // Aufnahme noch in der Pipeline ist. Weg, sobald der Server sie selbst als
  // `processing` meldet ODER ein Eintrag existiert, der NACH dem Stoppen
  // erzeugt wurde (= gelandet) — über den Zeitstempel, robust egal wann man
  // schaut.
  const since = processingSince();
  let showOptimistic = false;
  if (since !== null) {
    const landed = data.days.some((d) =>
      d.entries.some((e) => e.createdAt && Date.parse(e.createdAt) > since),
    );
    if (processing.length > 0 || landed) clearRecordingProcessing();
    else showOptimistic = processing.length === 0;
  }
  const up = showOptimistic
    ? describeUpload()
    : { uploading: false, pending: 0 };

  // Optimistik (inkl. Upload-Zustand) in die Signatur, damit Ein-/Ausblenden
  // und der Wechsel „hochgeladen → verarbeitet" auch ohne Datenänderung neu
  // zeichnet.
  const signature =
    alltagSignature(data) +
    "|opt" +
    (showOptimistic ? `${up.uploading ? "u" : "v"}${up.pending}` : "0");

  // Hat sich beim Poll nichts geändert: gar nicht neu zeichnen.
  if (quiet && signature === lastSignature) {
    schedulePoll(el, processing.length > 0 || showOptimistic);
    return;
  }
  lastSignature = signature;
  ensureOpenForToday(data.days);

  // Neuen Inhalt komplett in EINEM Fragment aufbauen und in einem Rutsch
  // einsetzen — kein Zwischen-Leerzustand, kein Blinken.
  const frag = document.createDocumentFragment();
  // Sofort-Karte zuerst (direkt nach dem Stoppen, bevor der Server meldet).
  if (showOptimistic)
    frag.appendChild(buildProcessingCard({ title: "" }, up));
  // Eine wartende Aufnahme bekommt ihre Karte; mehrere eine gemeinsame Zeile.
  if (processing.length === 1) {
    frag.appendChild(buildProcessingCard(processing[0]));
  } else if (processing.length > 1) {
    frag.appendChild(buildProcessingSammelkarte(processing));
  }
  // 2026-07-18 (Martin): ausgefallene Aufnahmen sichtbar machen. Bewusst OHNE
  // Einfluss auf schedulePoll — diese Karten sind ein Dauerzustand, kein Lauf.
  for (const u of data.unprocessed ?? [])
    frag.appendChild(buildUnprocessedCard(u));

  if (data.days.length === 0 && processing.length === 0 && !showOptimistic) {
    const empty = document.createElement("p");
    empty.className = "material-empty";
    /*
     * ⚠️ 16.09.2026 — DIESER SATZ LUD ZU ETWAS EIN, DAS ES IM BROWSER NICHT
     *    GIBT.
     *
     * Martin, ueber Albert: *„Ich habe gesehen, dass Albert noch keine
     * Aufnahmen in Mein Alltag hat. Das bedeutet, dass er nur die PWA nutzt,
     * und dort kann man ja nicht, oder?"* — Genau so ist es. Nachgezaehlt:
     * Albert hat null Dateien vom Typ `recording` und kein Projekt
     * „Mein Alltag". Der Aufnahme-Knopf wird im Browser bewusst ausgeblendet
     * („Ausblenden statt enttaeuschen", app.ts).
     *
     * Er las trotzdem „nimm deine erste auf!" und suchte vermutlich den
     * Knopf. Eine Aufforderung ohne Weg ist schlimmer als keine: sie laesst
     * den Fehler beim Leser suchen.
     *
     * Jetzt sagt die Seite, WARUM es hier nichts zu tun gibt — und macht
     * daraus keinen Mangel des Nutzers.
     */
    /*
     * ⚠️ 22.09.2026 — DIESER SATZ WAR SEIT DEM 17.09. FALSCH.
     *    Er sagte „laeuft nur in der App", da konnte der Browser es laengst.
     *    Die Grenze ist eine andere und gehoert trotzdem genannt: am
     *    Telefon endet die Browser-Aufnahme beim Sperren des Bildschirms.
     */
    empty.textContent = !kannAufzeichnen()
      ? "Noch keine Alltags-Aufnahmen. Dieses Geraet gibt kein Mikrofon frei."
      : isRecorderAvailable()
        ? "Noch keine Alltags-Aufnahmen — nimm deine erste auf!"
        : "Noch keine Alltags-Aufnahmen — nimm deine erste auf! Im Browser läuft sie, solange die Seite offen bleibt; am Telefon endet sie beim Sperren des Bildschirms.";
    frag.appendChild(empty);
    list.replaceChildren(frag);
    return;
  }

  // Ticket 1631: VOR dem Bauen der Tage — buildDayBlock liest daraus.
  setzeVitalwerte(data.vitalwerte);
  listenSpieler.clear();
  for (const day of data.days) frag.appendChild(buildDayBlock(day));
  list.replaceChildren(frag);

  schedulePoll(el, processing.length > 0 || showOptimistic);
}

/** Karte für eine Aufnahme, die gerade verarbeitet wird (Transkript/Digest). */
/** Die Karte selbst — Punkt, Kopfzeile, Unterzeile. Eine Form, zwei Nutzer. */
function processingKarte(kopf: string, unter: string): HTMLElement {
  const card = document.createElement("div");
  card.className = "alltag-processing";

  const dot = document.createElement("span");
  dot.className = "alltag-processing-dot";
  card.appendChild(dot);

  const text = document.createElement("div");
  text.className = "alltag-processing-text";
  const head = document.createElement("p");
  head.className = "alltag-processing-head";
  head.textContent = kopf;
  text.appendChild(head);
  const sub = document.createElement("p");
  sub.className = "alltag-processing-sub";
  sub.textContent = unter;
  text.appendChild(sub);
  card.appendChild(text);

  return card;
}

function buildProcessingCard(
  item: { title: string },
  upload?: { uploading: boolean; pending: number },
): HTMLElement {
  const time = extractTimeFromTitle(item.title);
  const detail = upload?.uploading
    ? upload.pending > 0
      ? `noch ${upload.pending} Häppchen offen`
      : "wird übertragen"
    : "Transkript + Zusammenfassung entstehen";
  return processingKarte(
    upload?.uploading
      ? "Aufnahme wird hochgeladen …"
      : "Aufnahme wird verarbeitet …",
    time ? `${time} · ${detail}` : detail,
  );
}

/**
 * Mehrere wartende Aufnahmen — EINE Zeile statt einer Karte je Stueck.
 *
 * ⚠️ 09.09.2026. Martin, mit Bild von sieben identischen Karten: *„Kann man
 *    das gruppieren? Stimmt das ueberhaupt?"* Es stimmte — sieben echte
 *    Aufnahmen von 10:48 bis 11:16 warteten wirklich, alle an derselben
 *    Ursache gescheitert (Whisper-Fenster in Bytes, pre.2765).
 *
 *    Aber sieben Karten, die alle dasselbe sagen, sind keine sieben
 *    Auskuenfte — sie sind eine Auskunft, siebenmal. Sie fuellten den
 *    ganzen Bildschirm und verdeckten die Tage darunter.
 *
 * ⚠️ Die Zeitspanne bleibt drin. Ohne sie waere die Zusammenfassung
 *    weniger wert als die Einzelkarten: Woran man einen Stau erkennt, ist
 *    nicht die Zahl, sondern wie weit er zurueckreicht.
 */
function buildProcessingSammelkarte(
  items: ReadonlyArray<{ title: string }>,
): HTMLElement {
  const zeiten = items
    .map((i) => extractTimeFromTitle(i.title))
    .filter((t): t is string => Boolean(t))
    .sort();
  const von = zeiten[0];
  const bis = zeiten[zeiten.length - 1];
  const spanne =
    von && bis ? (von === bis ? von : `${von} bis ${bis}`) : "";
  const detail = "Transkript + Zusammenfassung entstehen";
  return processingKarte(
    `${items.length} Aufnahmen werden verarbeitet …`,
    spanne ? `${spanne} · ${detail}` : detail,
  );
}

/**
 * 2026-07-18 (Martin): Karte fuer eine Aufnahme, deren Verarbeitung
 * ausgefallen ist — Audio + Transkript liegen vor, die Zusammenfassung fehlt.
 * Bewusst KEIN pulsierender Punkt (es laeuft nichts) und bewusst ohne
 * Poll-Auslösung. Aufbau ansonsten identisch zu buildProcessingCard.
 */
function buildUnprocessedCard(item: {
  title: string;
  transcriptChars: number;
}): HTMLElement {
  const card = document.createElement("div");
  card.className = "alltag-processing alltag-unprocessed";

  const mark = document.createElement("span");
  mark.className = "alltag-unprocessed-mark";
  mark.textContent = "!";
  card.appendChild(mark);

  const text = document.createElement("div");
  text.className = "alltag-processing-text";

  const head = document.createElement("p");
  head.className = "alltag-processing-head";
  head.textContent = item.title || "Aufnahme";
  text.appendChild(head);

  const sub = document.createElement("p");
  sub.className = "alltag-processing-sub";
  const time = extractTimeFromTitle(item.title);
  const detail = "Aufnahme liegt vor, Zusammenfassung fehlt";
  sub.textContent = time ? `${time} · ${detail}` : detail;
  text.appendChild(sub);

  card.appendChild(text);
  return card;
}

// 2026-07-03 (Martin, #37): Verarbeitungs-Status pro Tag (Aufnahmen vs.
// Digests/Essenz) — zeigt Luecken. Quelle: GET /api/alltag/status.
const dayStatusMap = new Map<string, AlltagStatusDay>();

/**
 * WHOOP-Werte je Tag (Ticket 1631). Wie dayStatusMap: einmal aus der Antwort
 * gefuellt, beim Bauen der Tage gelesen — so muss die Tages-Funktion nicht
 * die ganze Antwort kennen.
 */
const vitalMap = new Map<
  string,
  { erholung?: number; hrv?: number; ruhepuls?: number; belastung?: number }
>();

function setzeVitalwerte(
  werte:
    | Record<
        string,
        { erholung?: number; hrv?: number; ruhepuls?: number; belastung?: number }
      >
    | undefined,
): void {
  vitalMap.clear();
  for (const [tag, v] of Object.entries(werte ?? {})) vitalMap.set(tag, v);
}
async function loadDayStatus(): Promise<void> {
  const st = await fetchAlltagStatus(40);
  if (!st) return; // Status ist Zusatz-Info — Fehler ignorieren
  dayStatusMap.clear();
  for (const day of st.days ?? []) dayStatusMap.set(day.date, day);
  // 2026-07-18 (Martin): Ausgangsstand fuer den Beobachtungs-Poll — so gilt
  // erst eine ECHTE Aenderung als Grund, die volle Liste nachzuladen.
  if (st.now) lastNowSignature = nowSignature(st.now);
}

function buildDayStatusBadge(dayKey: string): HTMLElement | null {
  const st = dayStatusMap.get(dayKey);
  if (!st || st.complete) return null; // vollstaendig -> ruhig, kein Badge
  const parts = dayStatusTeile(st);
  if (parts.length === 0) return null;
  const badge = document.createElement("span");
  badge.className = "alltag-day-status alltag-day-status--warn";
  badge.textContent = parts.join(" · ");
  badge.title = `Aufnahmen: ${st.recordings} · Auswertungen: ${st.digests}`;
  return badge;
}

/**
 * WHOOP-Werte des Tages als schmale Leiste (2026-08-07, Ticket 1631).
 *
 * Martin will beim Lesen einer Aufnahme sehen, wie sein Koerper an dem Tag
 * stand. Den Stress-Wert gibt die WHOOP-Schnittstelle nicht her — die HRV
 * ist der engste Ersatz: niedrig heisst angespannt, hoch heisst erholt.
 *
 * Bewusst NEBEN der Stimmung in der Kopfzeile, nicht als eigener Block:
 * Der Zusammenhang zwischen dem, was er gesagt hat, und dem, was sein
 * Koerper meldete, entsteht erst, wenn beides nebeneinander steht.
 *
 * Gibt null zurueck, wenn es fuer den Tag nichts gibt — eine leere Leiste
 * waere schlechter als keine.
 */
/**
 * Stimmung und Koerper eines Tages — je eine Zeile (2026-08-29).
 *
 * ## Warum die gezeichnete Linie wieder weg ist
 *
 * Heute frueh stand hier ein Stimmungs-BOGEN: die Woerter des Tages als
 * Punkte auf einer Hoehenachse, verbunden zu einer Kurve. Martin, mit
 * Foto: *„Viel sagt der Strich jetzt aber nicht aus!! Schau mal auf die
 * anderen Tage, das kann wesentlich kompakter werden."*
 *
 * Er hat recht, und der Grund ist messbar. Die Hoehe kam aus einer Tabelle
 * von 27 Woertern; was nicht darin stand, fiel auf 0.5. Gezaehlt ueber 400
 * Alltag-Notizen:
 *
 *   Stimmungs-Angaben gesamt      296
 *   verschiedene Woerter          106
 *   davon in der Tabelle           13
 *   Angaben, die auf 0.5 fallen   227  = 77 %
 *
 * Der 28. August auf seinem Foto ist der Beleg: *inspiriert*, *offen*,
 * *aufgewuehlt* — kein einziges Wort in der Tabelle, alle drei auf
 * derselben Hoehe. Die Linie war nicht zufaellig flach, sie war
 * KONSTRUKTIONSBEDINGT flach.
 *
 * ⚠️ Die Tabelle nachzupflegen haette es nicht geheilt. Die Stimmung
 * schreibt ein Sprachmodell frei — das Vokabular ist offen, naechste Woche
 * stehen neue Woerter darin. Und selbst bei perfekter Abdeckung waere die
 * Hoehe meine erfundene Anordnung, keine Messung; das stand schon im
 * alten Kommentar. Ein Diagramm, das zugibt, nichts zu messen, auf einer
 * Zuordnung, die raet, ist Dekoration — und kostete die halbe Karte.
 *
 * Die WOERTER sind die Aussage. Sie bleiben, die Zeichnung geht.
 */
function buildStimmungszeile(moods: string[]): HTMLElement {
  const el = document.createElement("div");
  el.className = "alltag-stimmungszeile";
  // Mehr als drei Stationen passen auf 390 Punkte nicht nebeneinander; die
  // Mitte vertritt ein Auslassungszeichen. Die Reihenfolge ist chronologisch
  // — die Leserichtung zeigt den Verlauf, dafuer braucht es keinen Pfeil.
  const zeigen =
    moods.length <= 3 ? moods : [moods[0], "…", moods[moods.length - 1]];
  el.textContent = zeigen.join(" · ");
  return el;
}

/**
 * Die Koerperwerte des Tages als eine Zeile.
 *
 * Sie waren heute frueh drei umrandete Felder untereinander gestapelt —
 * zusammen mit dem Bogen rund 165 Punkte Kopfzeile pro Tag, bei drei Tagen
 * mehr als ein Bildschirm. Die Zahl bleibt in der Display-Serife (sie soll
 * man SEHEN, das war der Grund fuer die Felder), aber Rahmen, Polster und
 * Stapelung fallen weg.
 *
 * Gibt null zurueck, wenn es fuer den Tag nichts gibt — eine leere Zeile
 * waere schlechter als keine.
 */
function buildVitalLeiste(dayKey: string): HTMLElement | null {
  const v = vitalMap.get(dayKey);
  if (!v) return null;
  /*
   * ⚠️ Hier stand eine zweite Aufzaehlung derselben vier Werte, nur um zu
   * pruefen, ob ueberhaupt einer da ist. Zwei Listen ueber dieselbe Sache
   * laufen auseinander: die eine bekommt ein Feld dazu, die andere nicht.
   * `vitalFelder` weiss es bereits.
   */
  const felder = vitalFelder(v);
  if (felder.length === 0) return null;

  const el = document.createElement("div");
  el.className = "alltag-vitalzeile";
  for (const [zahl, einheit, name] of felder) {
    const feld = document.createElement("span");
    feld.className = "alltag-vitalwert";
    const wert = document.createElement("span");
    wert.className = "alltag-vitalwert-zahl";
    wert.textContent = zahl;
    if (einheit) {
      const e = document.createElement("span");
      e.className = "alltag-vitalwert-einheit";
      e.textContent = einheit;
      wert.appendChild(e);
    }
    const label = document.createElement("span");
    label.className = "alltag-vitalwert-name";
    label.textContent = name;
    if (name === "HRV") {
      feld.title =
        "Die HRV steht für die Anspannung — niedrig heißt angespannt, " +
        "hoch heißt erholt.";
    }
    feld.append(wert, label);
    el.appendChild(feld);
  }
  return el;
}

/** Die Werte als Tripel (Zahl, Einheit, Name) — hoechstens drei. */
function vitalFelder(v: {
  erholung?: number;
  hrv?: number;
  belastung?: number;
  ruhepuls?: number;
}): Array<[string, string, string]> {
  const alle: Array<[string, string, string]> = [];
  // Reihenfolge nach Aussagekraft: erst wie erholt, dann der Stress-Ersatz.
  if (typeof v.erholung === "number") alle.push([String(v.erholung), "\u2009%", "Erholung"]);
  if (typeof v.hrv === "number") alle.push([String(v.hrv), "\u2009ms", "HRV"]);
  if (typeof v.ruhepuls === "number") alle.push([String(v.ruhepuls), "", "Ruhepuls"]);
  if (typeof v.belastung === "number") alle.push([String(v.belastung), "", "Belastung"]);
  // ⚠️ Drei passen nebeneinander auf 390 Punkte. Ein viertes Feld wuerde
  // umbrechen und die Zeile kippen — lieber weglassen als quetschen.
  return alle.slice(0, 3);
}

function buildDayBlock(day: AlltagDay): HTMLElement {
  // Jeder Tag ist aufklappbar (<details>). Der offene Zustand wird in
  // openDayKeys gemerkt, damit er Neu-Aufbauten übersteht.
  const block = document.createElement("details");
  block.className = "alltag-day";
  block.open = openDayKeys.has(day.dayKey);
  block.addEventListener("toggle", () => {
    if (block.open) {
      openDayKeys.add(day.dayKey);
      baue(); // aufgegangen ohne Tipp (Programm, Tastatur): spätestens jetzt
    } else openDayKeys.delete(day.dayKey);
  });

  // Klappbarer Header: Datum links, Anzahl Aufnahmen rechts.
  const summary = document.createElement("summary");
  summary.className = "alltag-day-label";
  const label = document.createElement("span");
  label.className = "alltag-day-label-text";
  label.textContent = formatDayKey(day.dayKey);
  summary.appendChild(label);
  const n = day.entries.length;
  // #35: leere Tage (nur Rueckblick, 0 Eintraege) zeigen KEINEN sinnlosen
  // "0 Aufnahmen"-Zaehler mehr.
  if (n > 0) {
    const count = document.createElement("span");
    count.className = "alltag-day-count";
    count.textContent = n === 1 ? "1 Aufnahme" : `${n} Aufnahmen`;
    summary.appendChild(count);
  }
  // 2026-07-03 (Martin): Verarbeitungs-Luecke sichtbar (nur wenn vorhanden).
  const statusBadge = buildDayStatusBadge(day.dayKey);
  if (statusBadge) summary.appendChild(statusBadge);
  // 2026-07-08 (Martin): Stimmungs-Verlauf des Tages — chronologisch,
  // aufeinanderfolgende Wiederholungen dedupliziert.
  {
    const chrono = [...day.entries].sort((a, b) =>
      (extractTimeFromTitle(a.title) ?? "").localeCompare(
        extractTimeFromTitle(b.title) ?? "",
      ),
    );
    const moods: string[] = [];
    for (const e of chrono) {
      const m = (e.mood ?? "").trim();
      if (!m) continue;
      if (moods.length && moods[moods.length - 1].toLowerCase() === m.toLowerCase())
        continue;
      moods.push(m);
    }
    if (moods.length > 0) {
      summary.appendChild(buildStimmungszeile(moods));
    }
  }
  // 2026-08-07 (Ticket 1631): Wie der Koerper an dem Tag stand.
  const vital = buildVitalLeiste(day.dayKey);
  if (vital) summary.appendChild(vital);
  block.appendChild(summary);
  // Vor dem Umschalten bauen: `toggle` kommt erst nach dem Zeichnen — der Tag ginge leer auf.
  summary.addEventListener("click", () => baue());

  let gebaut = false;
  const baue = (): void => {
    if (gebaut) return;
    gebaut = true;
    spielerImBau = null;
    block.appendChild(baueTagesInhalt(day));
    if (spielerImBau) ersterSpielerJeTag.set(block, spielerImBau);
    spielerImBau = null;
  };
  baueTag.set(block, baue);
  tagHatVorlesen.set(block, !!alltagProjectId && day.entries.some((e) => vorleseKapitel(e, e.summary ? cleanText(e.summary) : "").length > 0));
  if (block.open) baue();
  return block;
}

/**
 * Die Kapitel der Vorlese-Fassung einer Aufnahme — EIN Ort für den Player der Karte und für die Frage, ob ein
 * Tag überhaupt etwas vorzulesen hat (`alltagNeuestenAbspielen`, ohne den Tag zu bauen).
 * Überblick nur als eigenes Kapitel, wenn es keine Themen-Abschnitte gibt (2026-07-01, sonst doppelt).
 */
function vorleseKapitel(entry: AlltagEntry, cleanedSummary: string): Array<{ title: string; text: string }> {
  const chapters: Array<{ title: string; text: string }> = [];
  for (const s of entry.sections ?? []) {
    const t = cleanText(s.text);
    if (t.trim()) chapters.push({ title: s.heading, text: t });
  }
  if (chapters.length === 0 && cleanedSummary.trim()) chapters.push({ title: "Überblick", text: cleanedSummary });
  return chapters;
}

/** Der Inhalt eines Tages: Zusammenfassung und Zeit-Spur mit allen Aufnahmen. */
function baueTagesInhalt(day: AlltagDay): HTMLElement {
  const body = document.createElement("div");
  body.className = "alltag-day-body";

  // Tageszusammenfassung als Schlagzeile (auch ohne Rollup → „Generieren").
  body.appendChild(buildRollupSection(day));

  // Aufnahmen NEUESTE ZUERST (oben), in Tagesabschnitte gruppiert, auf einer
  // vertikalen Zeit-Spur (Timeline) → Abend oben, Vormittag unten.
  const sorted = [...day.entries].sort((a, b) =>
    (extractTimeFromTitle(b.title) ?? "").localeCompare(
      extractTimeFromTitle(a.title) ?? "",
    ),
  );
  const timeline = document.createElement("div");
  timeline.className = "alltag-timeline";
  let lastBucket = "";
  let group: HTMLElement | null = null;
  let prevEntry: AlltagEntry | null = null;
  for (const entry of sorted) {
    const bucket = timeOfDay(extractTimeFromTitle(entry.title));
    if (bucket !== lastBucket || group === null) {
      lastBucket = bucket;
      group = document.createElement("div");
      group.className = "alltag-tl-group";
      const gh = document.createElement("div");
      gh.className = "alltag-tl-group-head";
      gh.textContent = bucket;
      group.appendChild(gh);
      timeline.appendChild(group);
    }
    // 2026-07-13 (Martin, Reformation): Still-Phase zwischen der vorigen
    // (neueren, darueber) und dieser (aelteren, darunter) Aufnahme —
    // nahtlose Zeitachse: Sprech- vs Still-Zeiten sichtbar.
    const still = prevEntry ? buildStillLine(prevEntry, entry) : null;
    if (still) group.appendChild(still);
    const item = document.createElement("div");
    item.className = "alltag-tl-item";
    item.appendChild(buildEntryCard(entry));
    group.appendChild(item);
    prevEntry = entry;
  }
  body.appendChild(timeline);
  return body;
}

/**
 * Optische Still-Phase zwischen zwei aufeinanderfolgenden Aufnahmen. `newer`
 * ist der (zeitlich spaetere) Eintrag darueber, `older` der darunter. Zeigt
 * "Still \u00b7 X" wenn die Luecke zwischen Ende der aelteren und Start der
 * neueren Aufnahme spuerbar ist (>= 5 min). Gibt null zurueck, wenn Zeit-
 * stempel fehlen (Altdaten) oder die Luecke zu klein ist (durchgehend).
 */
function buildStillLine(
  newer: AlltagEntry,
  older: AlltagEntry,
): HTMLElement | null {
  const startNewer = newer.recordedAt ? Date.parse(newer.recordedAt) : NaN;
  const endOlder = older.recordedUntil ? Date.parse(older.recordedUntil) : NaN;
  if (!Number.isFinite(startNewer) || !Number.isFinite(endOlder)) return null;
  const gapMs = startNewer - endOlder;
  const MIN_GAP_MS = 5 * 60 * 1000; // < 5 min gilt als durchgehend
  if (gapMs < MIN_GAP_MS) return null;
  const el = document.createElement("div");
  el.className = "alltag-tl-still";
  const txt = document.createElement("span");
  txt.className = "alltag-tl-still-text";
  txt.textContent = `Still \u00b7 ${formatGap(gapMs)}`;
  el.appendChild(txt);
  return el;
}

/** Formatiert eine Zeitspanne knapp: "12 min", "1 Std 20 min", "3 Std". */
function formatGap(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} Std` : `${h} Std ${m} min`;
}

/** Ordnet eine Uhrzeit (HH:MM) einem Tagesabschnitt zu (Timeline-Gruppen). */
function timeOfDay(hhmm: string | null): string {
  const h = hhmm ? parseInt(hhmm.slice(0, 2), 10) : NaN;
  if (Number.isNaN(h)) return "Ohne Uhrzeit";
  if (h < 12) return "Vormittag";
  if (h < 18) return "Nachmittag";
  return "Abend";
}

/**
 * Tageszusammenfassung-Sektion: Titel + „Neu generieren"-Knopf, Text und
 * Vorlese-Player. Der Knopf ersetzt den Roll-up serverseitig (force) und
 * aktualisiert Text + Player in-place (kein Komplett-Neuladen der Liste).
 */
function buildRollupSection(day: AlltagDay): HTMLElement {
  const rollupEl = document.createElement("div");
  rollupEl.className = "alltag-rollup";

  // Kopfzeile: Titel links, „Neu generieren" rechts.
  const headRow = document.createElement("div");
  headRow.className = "alltag-rollup-head";
  const rollupHeading = document.createElement("p");
  rollupHeading.className = "alltag-rollup-heading";
  rollupHeading.textContent = "Tageszusammenfassung";
  headRow.appendChild(rollupHeading);

  const hasRollup = !!day.rollup && day.rollup.trim().length > 0;

  const regenBtn = document.createElement("button");
  regenBtn.type = "button";
  regenBtn.className = "alltag-rollup-regen";
  regenBtn.textContent = hasRollup ? "Neu generieren" : "Generieren";
  headRow.appendChild(regenBtn);
  rollupEl.appendChild(headRow);

  const rollupBody = document.createElement("p");
  rollupBody.className = "alltag-rollup-body";
  if (hasRollup) {
    rollupBody.textContent = cleanText(day.rollup ?? "");
  } else {
    rollupBody.classList.add("is-empty");
    rollupBody.textContent = "Noch keine Zusammenfassung für diesen Tag.";
  }
  rollupEl.appendChild(rollupBody);

  // Player-Halter: wird beim Neu-Generieren mit frischem Text getauscht.
  const playerHolder = document.createElement("div");
  rollupEl.appendChild(playerHolder);
  const renderPlayer = (text: string): void => {
    playerHolder.replaceChildren();
    if (text.trim()) {
      playerHolder.appendChild(
        buildAudioPlayer(() => prepareTtsUrl(text), {
          kompakt: true,
          titel: "Zusammenfassung",
        }),
      );
    }
  };
  if (hasRollup) renderPlayer(rollupBody.textContent);

  regenBtn.addEventListener("click", () => {
    void (async () => {
      regenBtn.disabled = true;
      regenBtn.textContent = "Generiere …";
      try {
        const fresh = cleanText(await regenerateAlltagRollup(day.dayKey));
        rollupBody.textContent = fresh;
        renderPlayer(fresh);
        regenBtn.textContent = "Neu generieren";
      } catch {
        regenBtn.textContent = "Erneut versuchen";
      } finally {
        regenBtn.disabled = false;
      }
    })();
  });

  return rollupEl;
}

function buildEntryCard(entry: AlltagEntry): HTMLElement {
  const card = document.createElement("div");
  card.className = "alltag-entry";

  // Kopfzeile: nur Uhrzeit (HH:MM) — der Tag steht im Block-Header.
  const head = document.createElement("div");
  head.className = "alltag-entry-head";
  const time = extractTimeFromTitle(entry.title);
  const timeEl = document.createElement("span");
  timeEl.className = "alltag-entry-time";
  // 2026-07-20 (Martin, "damit ich das Zeitfenster besser erfassen kann"):
  // Endzeit aus recordedUntil anzeigen -- der Eintrag deckt einen Zeitraum
  // ab, nicht einen Moment. Nur wenn die Endzeit von der Startzeit abweicht.
  let timeText = time ?? "Aufnahme";
  if (time && entry.recordedUntil) {
    const untilMs = Date.parse(entry.recordedUntil);
    if (Number.isFinite(untilMs)) {
      const d = new Date(untilMs);
      const untilTxt =
        String(d.getHours()).padStart(2, "0") +
        ":" +
        String(d.getMinutes()).padStart(2, "0");
      if (untilTxt !== time) timeText = time + " \u2013 " + untilTxt;
    }
  }
  timeEl.textContent = timeText;
  head.appendChild(timeEl);

  // 2026-07-12 (Martin): Ort der Aufnahme (fuer "wo mit welcher Energie").
  // Zeigt den Namen, sonst die gerundeten Koordinaten; nichts ohne Fix.
  const loc = entry.location;
  if (loc) {
    const locEl = document.createElement("span");
    locEl.className = "alltag-entry-loc";
    const label = loc.place
      ? loc.place
      : `${loc.lat.toFixed(3)}, ${loc.lon.toFixed(3)}`;
    locEl.textContent = label;
    locEl.title = `Ort der Aufnahme: ${label}`;
    head.appendChild(locEl);
  }

  // Löschen (Martins Wunsch: nicht gebrauchte Aufnahmen entfernen) — mit Abfrage.
  const delBtn = document.createElement("button");
  delBtn.type = "button";
  delBtn.className = "alltag-entry-del";
  delBtn.setAttribute("aria-label", "Eintrag löschen");
  delBtn.title = "Eintrag löschen";
  delBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M9 3h6l1 2h4v2H4V5h4l1-2zm-3 6h12l-1 12H7L6 9zm4 2v8h2v-8h-2z"/></svg>';
  delBtn.addEventListener("click", () => {
    void (async () => {
      const ok = await confirmSheet({
        title: "Eintrag löschen?",
        message:
          "Aufnahme + Transkript werden entfernt. Das lässt sich nicht rückgängig machen.",
        confirmLabel: "Löschen",
        danger: true,
      });
      if (!ok) return;
      try {
        await deleteAlltagEntry(entry.id);
        // Nur das Element entfernen (kein Komplett-Neuladen). Wird der Tag-Block
        // dadurch leer, ihn ebenfalls entfernen.
        const block = card.parentElement;
        card.remove();
        if (block && !block.querySelector(".alltag-entry")) block.remove();
      } catch {
        // Nur im Fehlerfall neu laden → zeigt den tatsächlichen Stand.
        if (sheetEl && !sheetEl.hidden) void renderList(sheetEl);
      }
    })();
  });
  head.appendChild(delBtn);

  // 2026-07-08 (Martin, "nachbereiten"): Sprecher-Erkennung (Stimmprobe) neu
  // anwenden + Eintrag neu verdichten. Liegt das Ergebnis schon vor, geht es
  // in Sekunden; sonst laeuft die Analyse einige Minuten im Hintergrund.
  const reBtn = document.createElement("button");
  reBtn.type = "button";
  reBtn.className = "alltag-entry-del alltag-entry-redo";
  reBtn.title = "Neu analysieren (Sprecher + Zusammenfassung)";
  reBtn.setAttribute("aria-label", "Neu analysieren");
  reBtn.innerHTML =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>';
  reBtn.addEventListener("click", () => {
    void (async () => {
      reBtn.disabled = true;
      reBtn.classList.add("is-busy");
      reBtn.title = "Analysiere …";
      const reload = () => {
        if (sheetEl && !sheetEl.hidden) void renderList(sheetEl);
      };
      try {
        const r = await fetch(
          `/api/alltag/${encodeURIComponent(entry.id)}/speakers`,
          { method: "POST", credentials: "same-origin" },
        );
        const j = (await r.json()) as { ok?: boolean; status?: string };
        if (!r.ok || !j.ok) throw new Error(j.status ?? "failed");
        if (j.status === "redigested") {
          reload();
          return;
        }
        // Hintergrund-Job (einige Minuten): alle 20s nachsehen, dann neu laden.
        // 2026-07-28 (Akku, #744): Diese Schleife hatte KEIN Ende. Meldete der
        // Job weder "done" noch "error" — Absturz, Neustart, verlorene
        // Session —, fragte sie bis zum Schliessen der App alle 20 Sekunden
        // nach. Jede dieser Anfragen weckt den Funk, und niemand haette es
        // bemerkt, weil im Vordergrund nichts Sichtbares passiert.
        // Jetzt: Deckel bei 15 Minuten (45 Versuche) und Pause, solange
        // niemand hinschaut — die Antwort interessiert erst wieder, wenn die
        // Ansicht sichtbar ist.
        const POLL_MAX = 45;
        let pollVersuche = 0;
        const poll = window.setInterval(() => {
          void (async () => {
            if (document.visibilityState !== "visible") return;
            if (++pollVersuche > POLL_MAX) {
              window.clearInterval(poll);
              reBtn.disabled = false;
              reBtn.classList.remove("is-busy");
              reBtn.title = "Dauert ungewöhnlich lange — später erneut versuchen";
              return;
            }
            try {
              const s = await fetch(
                `/api/alltag/${encodeURIComponent(entry.id)}/speakers`,
                { credentials: "same-origin" },
              );
              const sj = (await s.json()) as { status?: string };
              if (sj.status === "done") {
                window.clearInterval(poll);
                // kleiner Puffer: das Neu-Verdichten nach dem Job braucht
                // noch ein paar Sekunden.
                window.setTimeout(reload, 15_000);
              } else if (sj.status === "error") {
                window.clearInterval(poll);
                reBtn.disabled = false;
                reBtn.classList.remove("is-busy");
                reBtn.title = "Fehler — erneut versuchen";
              }
            } catch {
              /* weiterpollen */
            }
          })();
        }, 20_000);
      } catch {
        reBtn.disabled = false;
        reBtn.classList.remove("is-busy");
        reBtn.title = "Fehler — erneut versuchen";
      }
    })();
  });
  head.insertBefore(reBtn, delBtn);

  card.appendChild(head);

  // Summary-Text (kurz gefasst) zum Lesen. Der separate Vorlese-Player fällt
  // weg — die Zusammenfassung wird Kapitel 0 des EINEN Players unten.
  const cleanedSummary = entry.summary ? cleanText(entry.summary) : "";
  if (cleanedSummary) {
    const summaryEl = document.createElement("p");
    summaryEl.className = "alltag-summary";
    summaryEl.textContent = cleanedSummary;
    card.appendChild(summaryEl);
  }

  // EIN Kapitel-Player pro Aufnahme: Kapitel 0 = „Überblick" (Zusammenfassung),
  // danach die Themen. Am Stück durchhören oder mit ◀▶ einzeln. Ersetzt die
  // früheren Einzel-Player (entrümpelt die Karte).
  const chapters = vorleseKapitel(entry, cleanedSummary);
  const builder = getViewerPlayerBuilder();
  if (builder && alltagProjectId && chapters.length > 0) {
    const player = builder({
      endpoint: `/api/projects/${encodeURIComponent(alltagProjectId)}/narrate`,
      getBody: (voiceId: string) => ({
        text: chapters[0]?.text ?? "",
        voiceId,
      }),
      sectionable: true,
      resumeId: `alltag:${entry.id}`,
      resumeVersion: String(chapters.length),
    });
    player.setSections(chapters);
    // 2026-07-19 (Traegheits-Jagd): KEIN prefetch() mehr beim Rendern.
    // Der Vorwaermer liest den KOMPLETTEN TTS-Stream (warmUrl) — bei jeder
    // Renderwelle fuer JEDE Karte. Gemessen an einem Vormittag: 1105
    // tts/stream-Abrufe (72 MB) in Bursts von 160 Synthesen pro Sekunde,
    // 11 Wellen, zeitgleich mit den WebView-Neustarts durch Speicherdruck.
    // Der erste Play-Klick loest jetzt regulaer auf (sichtbarer Ladezustand);
    // danach ist es serverseitig gecacht.
    player.setReady(true);
    card.appendChild(player.el);
    if (!spielerImBau) spielerImBau = player;
    listenSpieler.add(player);
  }

  // To-dos sichtbar (kurz + nützlich).
  // Themen-Labels SICHTBAR (Martins Kurzüberschriften — klare Übersicht, NICHT
  // hinter „Mehr" versteckt; das war zu viel verschachtelt → mehr Ordnung).
  if (entry.sections && entry.sections.length > 0) {
    const wrap = document.createElement("div");
    wrap.className = "alltag-sections";
    for (const sec of entry.sections) wrap.appendChild(buildSectionBlock(sec));
    card.appendChild(wrap);
  }

  // To-dos sichtbar.
  if (entry.todos.length > 0) {
    const todosEl = document.createElement("ul");
    todosEl.className = "alltag-todos";
    for (const todo of entry.todos) {
      const li = document.createElement("li");
      li.className = "alltag-todo-item";
      li.textContent = todo;
      todosEl.appendChild(li);
    }
    card.appendChild(todosEl);
  }

  // „Mehr" nur noch fürs Rohe: Original-Aufnahme + Sprecher + Roh-Transkript.
  if (entry.audioUrl || entry.transcriptFileId) {
    const det = document.createElement("details");
    det.className = "alltag-transcript-details alltag-detail-merged";
    const sm = document.createElement("summary");
    sm.className = "alltag-transcript-toggle";
    sm.textContent = "Original-Aufnahme & Transkript";
    det.appendChild(sm);
    // 2026-07-19 (Traegheits-Jagd, gemessen 1,63 GB/Vormittag): Player und
    // Sprecher-Bereich ERST beim ersten Aufklappen bauen. Vorher entstanden
    // pro Renderwelle 161 Audio-Elemente (preload=metadata -> iOS zieht
    // Range-Requests auf die Roh-Aufnahmen; dieselbe 26-MB-Datei bis zu 98x
    // an einem Vormittag) plus 161 Sprecher-Statusabfragen — alles fuer
    // einen zugeklappten Bereich. Gleiches Muster wie das Transkript unten.
    // Zeigt der Sprecher-Abschnitt einen Wechsel, tritt das Transkript
    // zurück — ein Text, nicht zwei (siehe buildSpeakerSection).
    let sprecherWechsel = false;
    let tBodyRef: HTMLElement | null = null;
    const mediaAudioUrl = entry.audioUrl;
    if (mediaAudioUrl) {
      const mediaHolder = document.createElement("div");
      det.appendChild(mediaHolder);
      let mediaBuilt = false;
      det.addEventListener("toggle", () => {
        if (!det.open || mediaBuilt) return;
        mediaBuilt = true;
        const knownDur = durationFromRange(
          entry.recordedAt,
          entry.recordedUntil,
        );
        mediaHolder.appendChild(
          buildAudioPlayer(mediaAudioUrl, {
            durationSec: knownDur,
            incomplete: knownDur <= 0,
            // 2026-09-04: nur der Knopf — Titel, Uhr und Anhalten stehen in
            // der einen Abspielleiste. Vorher trug jeder aufgeklappte
            // Eintrag seine eigene Leiste mit eigener Uhr.
            kompakt: true,
            // 05.09. (Martin: "dass man weiss, was man genau hoert"):
            // Der Eintragstitel allein sagt nicht, WOHER er kommt.
            titel: entry.title
              ? `Mein Alltag · ${entry.title}`
              : "Mein Alltag",
          }),
        );
        mediaHolder.appendChild(
          buildSpeakerSection(entry, (mehrere) => {
            sprecherWechsel = mehrere;
            if (tBodyRef) tBodyRef.hidden = mehrere;
          }),
        );
      });
    }
    // 2026-07-18 (Martin, Ladezeiten): Roh-Transkript ERST beim Aufklappen
    // holen. Vorher kam es fuer JEDEN Eintrag mit der Liste mit (~1,9 MB
    // von 2,3 MB) und wurde ins DOM gebaut, obwohl dieser Bereich fast nie
    // geoeffnet wird.
    const tFileId = entry.transcriptFileId;
    if (tFileId) {
      const tBody = document.createElement("div");
      tBody.className = "alltag-transcript-body";
      tBody.hidden = sprecherWechsel;
      tBodyRef = tBody;
      det.appendChild(tBody);
      let tLoaded = false;
      det.addEventListener("toggle", () => {
        if (!det.open || tLoaded) return;
        tLoaded = true;
        tBody.textContent = "Transkript wird geladen \u2026";
        void fetch(
          `/api/projects/${encodeURIComponent(alltagProjectId)}/files/${encodeURIComponent(tFileId)}`,
          { credentials: "same-origin" },
        )
          .then((r) =>
            r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
          )
          .then((j: { content?: string }) => {
            tBody.textContent = "";
            const text = typeof j.content === "string" ? j.content : "";
            if (!text.trim()) {
              tBody.textContent = "Kein Transkript vorhanden.";
              return;
            }
            for (const para of formatTranscript(cleanText(text))) {
              const pEl = document.createElement("p");
              pEl.textContent = para;
              tBody.appendChild(pEl);
            }
            kuerzeLangenText(tBody);
          })
          .catch(() => {
            tLoaded = false; // erneutes Aufklappen darf es nochmal versuchen
            tBody.textContent = "Transkript konnte nicht geladen werden.";
          });
      });
    }
    card.appendChild(det);
  }

  return card;
}

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------

/**
 * Wie lange jemand gesprochen hat — knapp, und nie „0 min". (23.09.2026)
 *
 * Martin: *„Warum steht hier 0 Minuten? Überall bei den Sprechern steht
 * 0 Minuten."* `Math.round(25 / 60)` ist 0 — jede Aufnahme unter einer
 * halben Minute sprach so von niemandem. Die zweite Stelle (offene Stimmen)
 * rechnete für sich selbst und rundete auf mindestens 1 auf; jetzt rechnen
 * beide HIER.
 */
export function sprechdauer(sekunden: number): string {
  const s = Number.isFinite(sekunden) && sekunden > 0 ? sekunden : 0;
  if (s < 59.5) return `${Math.max(1, Math.round(s))} s`;
  const min = Math.round(s / 60);
  if (min < 90) return `${min} min`;
  return `${(min / 60).toFixed(1).replace(".", ",")} h`;
}

/**
 * Langer Text steht gekürzt da, mit „Ganzen Text zeigen". (23.09.2026)
 *
 * Martin: *„Was ist, wenn es mehr Text ist?"* Bisher bekam das Transkript
 * ein eigenes Scrollfenster (höchstens 40 % der Bildschirmhöhe) mitten in
 * der Seite — auf dem Telefon ein Fenster im Fenster, in dem der Daumen
 * mal die Seite und mal das Transkript bewegt.
 *
 * ⚠️ Gemessen wird NACH dem Zeichnen: ob gekürzt werden muss, weiß erst der
 *    Browser. Ein kurzer Text bekommt keinen Knopf, der nichts täte.
 */
function kuerzeLangenText(el: HTMLElement): void {
  requestAnimationFrame(() => {
    if (!el.isConnected || el.dataset.gekuerzt === "1") return;
    el.classList.add("alltag-lang");
    if (el.scrollHeight <= el.clientHeight + 4) {
      el.classList.remove("alltag-lang");
      return;
    }
    el.dataset.gekuerzt = "1";
    const mehr = document.createElement("button");
    mehr.type = "button";
    mehr.className = "alltag-lang-mehr";
    mehr.textContent = "Ganzen Text zeigen";
    mehr.setAttribute("aria-expanded", "false");
    mehr.addEventListener("click", () => {
      const offen = el.classList.toggle("is-offen");
      mehr.textContent = offen ? "Weniger zeigen" : "Ganzen Text zeigen";
      mehr.setAttribute("aria-expanded", String(offen));
    });
    el.after(mehr);
  });
}

/**
 * Ein aufklappbarer Themen-Abschnitt (Schritt 2): Überschrift = Thema, Inhalt =
 * Text zum LESEN. Das Hören läuft über den Kapitel-Player oben (alle Themen am
 * Stück oder einzeln per ◀▶) — daher hier kein eigener Player mehr. Alle
 * Abschnitte sind standardmäßig zu (Überschriften = Inhaltsverzeichnis).
 */
function buildSectionBlock(sec: {
  heading: string;
  text: string;
}): HTMLElement {
  const det = document.createElement("details");
  det.className = "alltag-section";
  const sum = document.createElement("summary");
  sum.className = "alltag-section-head";
  // 2026-07-20 (Martin, "keine Zeilenumbrueche in solcherlei Hinsichten"):
  // ein Zeitfenster am Titelende wird als eigener, umbruchfester Baustein
  // gerendert statt als Fliesstext.
  const tm = /^(.*?)\s*[\u00b7]\s*(\d{1,2}:\d{2}\s*[\u2013-]\s*\d{1,2}:\d{2})\s*$/.exec(
    zeitSpanneKompakt(sec.heading),
  );
  if (tm && tm[1]) {
    sum.textContent = tm[1];
    const t = document.createElement("span");
    t.className = "alltag-section-time";
    t.textContent = (tm[2] ?? "").replace(/\s+/g, "");
    sum.appendChild(t);
  } else {
    sum.textContent = zeitSpanneKompakt(sec.heading);
  }
  det.appendChild(sum);

  const body = document.createElement("div");
  body.className = "alltag-section-body";
  const p = document.createElement("p");
  p.className = "alltag-section-text";
  p.textContent = cleanText(sec.text);
  body.appendChild(p);
  det.appendChild(body);
  return det;
}

// Ergebnis-Cache pro Eintrag — verhindert Refetch bei Poll-Neuaufbauten.
const speakerCache = new Map<string, SpeakerData>();

/**
 * Sprecher-Erkennung (Schritt 3): lädt AUTOMATISCH (Martins Entscheidung) beim
 * Rendern den Status — die Erkennung läuft serverseitig nach jeder Aufnahme.
 * Fertiges Ergebnis erscheint sofort („[Du] ↔ [Sprecher 2]"), laufendes zeigt
 * „wird erkannt …", und ein Knopf bleibt nur als Fallback (alte/kurze Aufnahmen).
 * Du wirst per Stimmabdruck erkannt, andere bleiben anonym (benennbar).
 */
function buildSpeakerSection(
  entry: AlltagEntry,
  /**
   * Meldet, ob MEHRERE Sprecher im Text vorkommen. (23.09.2026)
   *
   * ⚠️ Als Rückruf, nicht als DOM-Ereignis: aus dem Zwischenspeicher wird
   *    schon WÄHREND des Baus gezeichnet — da hängt der Abschnitt noch an
   *    nichts, und ein aufsteigendes Ereignis käme nirgends an.
   */
  onSprecher?: (mehrere: boolean) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "alltag-speakers";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "alltag-speaker-btn";
  btn.textContent = "Sprecher erkennen";
  const out = document.createElement("div");
  out.className = "alltag-speaker-result";
  const status = document.createElement("p");
  status.className = "alltag-speaker-status";
  status.hidden = true;
  wrap.append(btn, status, out);

  const render = (data: SpeakerData): void => {
    out.replaceChildren();
    const r = data.result;
    if (!r) return;
    const list = document.createElement("div");
    list.className = "alltag-speaker-list";
    // 2026-07-01 (Martin): pyannote splittet eine EINZELNE Stimme manchmal
    // in zwei Cluster; der zweite (ohne echten Redetext) erschien faelschlich
    // als "Sprecher 2". Nur Sprecher mit echtem Transkript-Anteil (oder Du).
    const speaking = new Set(r.turns.map((t) => t.speaker));
    for (const s of r.speakers) {
      if (!s.isMe && !speaking.has(s.label)) continue;
      const chip = document.createElement("span");
      chip.className = "alltag-speaker-chip" + (s.isMe ? " is-me" : "");
      const txt = document.createElement("span");
      txt.textContent = `${s.label} · ${sprechdauer(s.seconds)}`;
      chip.appendChild(txt);
      // Anonyme Sprecher benennen (→ Register + Wiedererkennung).
      if (!s.isMe && /^Sprecher\s/.test(s.label)) {
        const nameBtn = document.createElement("button");
        nameBtn.type = "button";
        nameBtn.className = "alltag-speaker-name-btn";
        nameBtn.textContent = "benennen";
        nameBtn.addEventListener("click", () => {
          const input = document.createElement("input");
          input.type = "text";
          input.className = "alltag-speaker-input";
          input.placeholder = "Name …";
          let done = false;
          const save = (): void => {
            if (done) return;
            const nm = input.value.trim();
            if (!nm) return;
            done = true;
            input.disabled = true;
            void nameSpeaker(entry.id, s.label, nm)
              .then((data) => {
                frageGlockeNeu();
                render(data);
              })
              .catch(() => {
                done = false;
                input.disabled = false;
              });
          };
          input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") save();
          });
          input.addEventListener("blur", save);
          nameBtn.replaceWith(input);
          input.focus();
        });
        chip.append(" ", nameBtn);
      }
      list.appendChild(chip);
    }
    out.appendChild(list);
    /*
     * ⚠️ 23.09.2026 — EIN Text, nicht zwei. Martin, mit Bild: derselbe Satz
     *    stand zweimal untereinander — einmal als Sprecher-Zeile (klein,
     *    ohne Satzzeichen), einmal als Transkript. Bei EINEM Sprecher sagt
     *    die Zeile nichts, was die Marke darüber nicht schon sagt; das
     *    Transkript ist der bessere Text. Erst ein Wechsel zwischen
     *    Stimmen ist eine eigene Information — dann zeigt der Abschnitt ihn,
     *    und das Transkript tritt zurück.
     */
    const mehrere = speaking.size >= 2;
    onSprecher?.(mehrere);
    if (!mehrere) return;
    const wechsel = document.createElement("div");
    wechsel.className = "alltag-speaker-turns";
    for (const t of r.turns) {
      const turn = document.createElement("p");
      turn.className = "alltag-speaker-turn";
      const lab = document.createElement("span");
      lab.className =
        "alltag-speaker-label" + (t.speaker === "Du" ? " is-me" : "");
      lab.textContent = t.speaker;
      const txt = document.createElement("span");
      txt.className = "alltag-speaker-text";
      txt.textContent = " " + tidyTranscript(t.text);
      turn.append(lab, txt);
      wechsel.appendChild(turn);
    }
    out.appendChild(wechsel);
    kuerzeLangenText(wechsel);
  };

  const poll = (): void => {
    window.setTimeout(() => {
      if (!wrap.isConnected) return; // Sheet zu / neu gerendert → aufhören
      void getSpeakerId(entry.id)
        .then((data) => {
          if (!wrap.isConnected) return;
          if (data.status === "done") {
            speakerCache.set(entry.id, data);
            btn.hidden = true;
            status.hidden = true;
            render(data);
          } else if (data.status === "error") {
            status.hidden = true;
            btn.hidden = false;
            btn.disabled = false;
            btn.textContent = "Sprecher erkennen";
          } else {
            poll();
          }
        })
        .catch(() => poll());
    }, 15000);
  };

  btn.addEventListener("click", () => {
    void (async () => {
      btn.disabled = true;
      btn.textContent = "Prüfe …";
      try {
        const data = await getSpeakerId(entry.id);
        if (data.status === "done") {
          btn.hidden = true;
          render(data);
          return;
        }
        if (data.status === "none" || data.status === "error") {
          await startSpeakerId(entry.id);
        }
        btn.textContent = "Erkenne Sprecher … (~10–15 min)";
        poll();
      } catch {
        btn.disabled = false;
        btn.textContent = "Fehler — erneut versuchen";
      }
    })();
  });

  // Automatisch laden — die Erkennung läuft serverseitig nach jeder Aufnahme.
  const apply = (data: SpeakerData): void => {
    speakerCache.set(entry.id, data);
    if (data.status === "done") {
      btn.hidden = true;
      status.hidden = true;
      render(data);
    } else if (data.status === "running") {
      btn.hidden = true;
      status.hidden = false;
      status.textContent = "Sprecher werden automatisch erkannt …";
      poll();
    } else {
      // none/error → Knopf als manueller Fallback (alte/kurze Aufnahmen).
      btn.hidden = false;
    }
  };
  const cached = speakerCache.get(entry.id);
  if (cached?.status === "done") {
    apply(cached);
  } else {
    btn.hidden = true; // verstecken, bis der Status da ist
    void getSpeakerId(entry.id)
      .then(apply)
      .catch(() => {
        btn.hidden = false; // Fallback-Knopf zeigen
      });
  }

  return wrap;
}

/** "2026-06-20" → "Fr, 20. Juni" (kompakter Tages-Header). */
function formatDayKey(dayKey: string): string {
  try {
    return new Date(dayKey + "T12:00:00").toLocaleDateString("de-AT", {
      weekday: "short",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dayKey;
  }
}

/**
 * Extrahiert Uhrzeit aus Titel wie "Alltag 20.06.2026, 14:30"
 * Gibt "14:30" zurück oder null.
 */
function extractTimeFromTitle(title: string): string | null {
  const m = /(\d{2}:\d{2})/.exec(title);
  return m?.[1] ?? null;
}

/**
 * Bereinigt Markdown-/HTML-Kommentar-Artefakte für die UI-Anzeige:
 * - HTML-Kommentare (<!-- ... -->)
 * - Markdown-Heading-Zeilen (# ...)
 * - Blockquote-Zeilen (> ...)
 * - Stimmungs-Markup (_Stimmung: ..._) → "Stimmung: ..."
 * - Whitespace-Normalisierung
 */
function cleanText(md: string): string {
  return md
    .replace(/<!--[\s\S]*?-->/g, "") // HTML-Kommentare entfernen
    .split("\n")
    .filter((l) => !l.match(/^#{1,6}\s/) && !l.startsWith(">"))
    .map((l) =>
      l
        // 2026-07-06 (Martin): rohes Markdown NIE anzeigen — Digest zeigte
        // "**Offene To-dos:**" und "* " als Klartext.
        .replace(/^\s*[*+-]\s+/, "• ") // Bullet -> echter Punkt
        .replace(/\*\*([^*]+)\*\*/g, "$1") // **fett** -> fett
        .replace(/(^|\s)\*([^*\n]+)\*(?=[\s.,:;!?]|$)/g, "$1$2") // *kursiv*
        .replace(/`([^`]+)`/g, "$1") // `code`
        .replace(/^_(.+)_$/, "$1")
        .trim(),
    )
    .filter(Boolean)
    .join("\n")
    .trim();
}

/**
 * #14: Transkript-Reinigung fürs LESEN — reduziert typische Roh-Transkript-
 * Störungen, OHNE den Inhalt zu verändern (Martins Faktentreue-Leitlinie: das
 * gespeicherte Original bleibt unangetastet, hier entsteht nur eine saubere
 * Lese-Fassung — gleiche Philosophie wie cleanText). Entfernt: Whisper-Stille-
 * Halluzinationen (ZDF/ORF/Amara-Untertitel etc.), nicht-lexikalische Fülllaute
 * (äh, ähm, öh, hm) und unmittelbare Stotter-Wiederholungen NUR bei Grammatik-
 * Wörtern ("das das" → "das"). Echte Wörter/Intensivierer bleiben erhalten.
 */
function tidyTranscript(text: string): string {
  let t = (text ?? "").replace(/\s+/g, " ");
  // Whisper-Stille-Halluzinationen (Spiegel der Server-Liste in alltag-digest).
  const hallu: RegExp[] = [
    /untertitel\w*(\s+(des|im auftrag des|von|der|nach|für))?\s*[^.\n]*\b(zdf|ard|wdr|ndr|swr|br|mdr|rbb|orf|srf|arte|amara)\b[^.\n]*/gi,
    /\buntertitel\w*\s+der\s+amara[^.\n]*/gi,
    /\bamara\.org[^.\n]*/gi,
    /\b(copyright|©)\s*\w+\s*\d{2,4}/gi,
    /\bvielen dank(\s+f(ü|ue)rs?\s+(zuschauen|zuhören|anschauen))?\.?/gi,
    /\buntertitel\w*\b\.?/gi,
  ];
  for (const re of hallu) t = t.replace(re, " ");
  // Nicht-lexikalische Fülllaute als eigenständige Tokens (umlaut-sicher ohne
  // \b: explizite Whitespace-/Satzzeichen-Grenzen): äh, ähm, öh, ehm, hm, mh.
  t = t.replace(/(^|\s)(äh+m?|öh+m?|ehm|hm+|mh+|mhm)(?=$|\s|[,.!?…])/gi, "$1");
  // Unmittelbare Stotter-Wiederholungen NUR bei Grammatik-Wörtern auf ein
  // Vorkommen reduzieren ("ich ich" → "ich"). Intensivierer (sehr sehr, ganz
  // ganz) bleiben bewusst unangetastet.
  t = t.replace(
    /\b(der|die|das|ich|du|er|sie|es|wir|ihr|und|ist|ein|eine|den|dem|auf|im|in|zu|mit|dass|weil|aber|oder)(\s+\1\b)+/gi,
    "$1",
  );
  // Aufräumen: Leerzeichen vor Satzzeichen, doppelte/führende Satzzeichen.
  return t
    .replace(/\s+([,.!?…])/g, "$1")
    .replace(/([,;])\s*([,.;!?…])/g, "$2")
    .replace(/\s+/g, " ")
    .replace(/^[\s,.;:!?…]+/, "")
    .trim();
}

/** #14: Roh-Transkript lesbarer machen — erst reinigen (tidyTranscript), dann in
 *  Absätze (~3 Sätze) gruppieren statt eine undifferenzierte Textwurst. */
function formatTranscript(text: string): string[] {
  // 2026-07-01 (Martin): robuster gegen interpunktionsarme Monologe -- an
  // Satzenden UND (bei ueberlangen Laeufen) an Kommagrenzen zerlegen, dann
  // nach Wort-Budget zu Absaetzen buendeln. Nur Absatzgrenzen, kein Inhalt.
  const cleaned = tidyTranscript(text).replace(/\s+/g, " ");
  if (!cleaned) return [];
  const HARD = 45;
  const rawUnits = cleaned.match(/[^.!?…]+[.!?…]+(?=\s|$)|\S[^.!?…]*$/g) ?? [
    cleaned,
  ];
  const atoms: string[] = [];
  const pushHard = (str: string): void => {
    const w = str.split(/\s+/);
    if (w.length <= HARD) {
      atoms.push(str);
      return;
    }
    for (let i = 0; i < w.length; i += HARD)
      atoms.push(w.slice(i, i + HARD).join(" "));
  };
  for (const u of rawUnits) {
    const str = u.trim();
    if (!str) continue;
    if (str.split(/\s+/).length <= HARD) {
      atoms.push(str);
      continue;
    }
    for (const seg of str.split(/(?<=,)\s+/)) {
      const t = seg.trim();
      if (t) pushHard(t);
    }
  }
  const paras: string[] = [];
  let buf: string[] = [];
  let words = 0;
  for (const at of atoms) {
    buf.push(at);
    words += at.split(/\s+/).length;
    if (words >= 40) {
      paras.push(buf.join(" "));
      buf = [];
      words = 0;
    }
  }
  if (buf.length) paras.push(buf.join(" "));
  return paras.filter(Boolean);
}

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

// 2026-06-30 (Martin): Beim Zurueckkommen in die App (Vordergrund) "Mein
// Alltag" sofort still aktualisieren — neue Aufnahmen, die waehrend der
// Abwesenheit fertig verarbeitet wurden, tauchen so von selbst auf.
let alltagVisibilityWired = false;
function wireAlltagVisibilityRefresh(): void {
  if (alltagVisibilityWired || typeof document === "undefined") return;
  alltagVisibilityWired = true;
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "visible" &&
      sheetEl &&
      !sheetEl.hidden
    ) {
      void renderList(sheetEl, { quiet: true });
    }
  });
}


/**
 * Eine Stimme hat einen Namen bekommen: die Glocke fragt neu (der Server hat die Frage dazu abgehakt, server/index.ts,
 * speakers/name). Ohne das stünde der Punkt bis zum nächsten Öffnen der App auf einer Frage, die beantwortet ist.
 * Wirft nie.
 */
function frageGlockeNeu(): void {
  void import("./glocke.js").then((m) => m.pruefeGlocke()).catch(() => undefined);
}

// 2026-07-14 (Martin, "Glocke bleibt offen beim Wechsel zur Aufnahme"):
// fremde Overlays (Glocke/Changelog) DOM-direkt schliessen — gleiches Muster
// wie closeAllOverlays in app.ts; der #328-isConnected-Guard macht das sicher.
function closeForeignOverlays(): void {
  try {
    document
      .querySelectorAll(".tickets-backdrop, .changelog-sheet")
      .forEach((el) => el.remove());
  } catch {
    /* best-effort */
  }
}

export function openAlltagSheet(): void {
  glockenFrage = null;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));
  closeForeignOverlays();
  // Liste ansehen ≠ live aufnehmen. Ein offenes Overlay würde sonst
  // weiterticken, obwohl Stop schon gedrückt war.
  document.querySelector(".alltag-rec-overlay")?.remove();
  wireAlltagVisibilityRefresh();
  if (!sheetEl) sheetEl = buildSheet();
  // 2026-07-05 (Martin): echtes Menue-Schliessen — der alte nav-menu-Hack
  // zeigte auf ein DOM, das es nicht mehr gibt (Menue blieb offen).
  requestMenuClose();
  sheetEl.hidden = false;
  darstellung = renderList(sheetEl);
  switchTab("tage");
  setPageHash("alltag");
}

/**
 * „Sprecher verwalten“ direkt öffnen — derselbe Weg wie der Eintrag im ⋯-Menü von „Mein Alltag“ (`switchTab("sprecher")`).
 * Dort stehen oben die benannten Stimmen und darunter „Noch nicht benannt“, mit dem Feld „Wer ist das?“. Die Glocke
 * führt mit „Benennen“ hierher (frontend/lib/glocke.ts, 07.10.2026) — vorher landete man auf dem Reiter „Tage“, und die
 * Stimmen standen zwei Schritte weiter.
 */
export function openSprecherSheet(opt: { frage?: string } = {}): void {
  openAlltagSheet();
  // Die Frage der Glocke: der Server stellt diese Stimme nach oben (auch jenseits der zwölf zuletzt gehörten), und die
  // Liste wird frisch geholt — ein Stand von vor ein paar Minuten kennt die Frage vielleicht noch nicht.
  glockenFrage = opt.frage ?? null;
  speakersLoadedAt = 0;
  switchTab("sprecher");
}

/** Warum nichts spielt, wenn Vespera eben „geht gleich los“ gesagt hat. */
export const NICHTS_ZUM_ABSPIELEN = "Nichts zum Abspielen: keine Aufnahme hier hat eine Vorlese-Fassung.";

/**
 * „Mein Alltag vorlesen“ (Ticket 2698): die Seite öffnen und den neuesten
 * Eintrag abspielen — über seinen eigenen play(), derselbe Weg wie ▶.
 * Sein Tag klappt auf, der Player rückt ins Bild. Gibt es keinen Player
 * (kein Eintrag mit Vorlese-Fassung), bleibt die Seite offen: false.
 *
 * ⚠️ Nachbesserung pre.3394: ohne Player sagt die Statuszeile der Seite,
 *    warum — Vespera hat den Start eben zugesagt. Steht dort schon ein
 *    Grund (der Abruf scheiterte), bleibt er stehen.
 */
export async function alltagNeuestenAbspielen(): Promise<boolean> {
  openAlltagSheet();
  await darstellung;
  // Die Tage von oben (neueste zuerst): der erste mit einem Player — zugeklappte werden dafür gebaut.
  let spieler: ViewerNarrationPlayer | null = null;
  for (const tag of sheetEl?.querySelectorAll<HTMLElement>(".alltag-list > details.alltag-day") ?? []) {
    // Ein Tag ohne Vorlese-Fassung bekommt keinen Player — ihn zu bauen hieße, für nichts zu bauen (pre.3621).
    if (!tagHatVorlesen.get(tag)) continue;
    baueTag.get(tag)?.();
    spieler = ersterSpielerJeTag.get(tag) ?? null;
    if (spieler) break;
  }
  if (!spieler?.play) {
    const status = sheetEl?.querySelector<HTMLElement>(".material-status");
    if (status && !status.textContent) status.textContent = NICHTS_ZUM_ABSPIELEN;
    return false;
  }
  const tag = spieler.el.closest("details");
  if (tag) tag.open = true;
  spieler.el.scrollIntoView?.({ block: "center" });
  spieler.play();
  return true;
}

/**
 * Dock „Aufnahme": die Aufzeichnungen. Overlay nur, wenn wirklich etwas
 * läuft — sonst startet ein Tipp die Aufnahme neu.
 */
export async function openAlltagAufnahmen(): Promise<void> {
  if (kannAufzeichnen()) {
    await syncFromNative();
    const st = getRecorderState();
    if (st === "recording" || st === "paused") {
      await openRecorderOverlay();
      return;
    }
  }
  openAlltagSheet();
}

export function closeAlltagSheet(): void {
  clearProcessingPoll();
  if (sheetEl) sheetEl.hidden = true;
  clearPageHash("alltag");
  leereVersteckteListe();
}

/** So oft schaut die geschlossene Seite nach, ob ihr Player still geworden ist. */
export const LEEREN_NACHSEHEN_MS = 2000;
let leerenZeit: ReturnType<typeof setTimeout> | null = null;

/** Arbeitet ein Player der Liste noch? Ohne `aktiv` (ältere Builder): spielt sein <audio>. */
function listeSpielt(): boolean {
  for (const p of listenSpieler) {
    if (!p.el.isConnected) continue;
    if (p.aktiv ? p.aktiv() : Array.from(p.el.querySelectorAll("audio")).some((a) => !a.paused && !a.ended)) return true;
  }
  return false;
}

/**
 * Die Tage einer geschlossenen Seite fallen aus dem DOM (pre.3620, s. `baueTag`). Arbeitet noch ein Player
 * darin, bleibt die Liste, bis er ZWEIMAL hintereinander still ist (`LEEREN_NACHSEHEN_MS` Abstand).
 *
 * ⚠️ Prüfbefund zu pre.3620: Dort wartete die Liste auf das nächste `pause`/`ended` des <audio>. Die Engine spielt
 *    eine Vorlese-Fassung in Stücken auf DEMSELBEN Element — am Stückende kommen `pause` und `ended`, das nächste
 *    Stück erst nach dem Laden. In dieser Lücke wurde geleert, der Ton lief im abgehängten Element weiter: Ton ohne
 *    sichtbaren Stopp, beim nächsten Öffnen zwei Stimmen. Jetzt zählt der Zustand des Players (Pause zählt als
 *    aktiv — sie kann vom Sperrbildschirm weitergehen), und zweimal still schließt die Lücke zwischen Abschnitten aus.
 */
function leereVersteckteListe(): void {
  if (leerenZeit !== null) clearTimeout(leerenZeit);
  leerenZeit = null;
  const el = sheetEl;
  if (!el?.hidden) return;
  if (listeSpielt()) warteBisStill(0);
  else leere(el);
}

/** Nachsehen, bis der Player zweimal hintereinander still ist; dann leeren. Öffnen bricht ab. */
function warteBisStill(stilleBlicke: number): void {
  leerenZeit = setTimeout(() => {
    leerenZeit = null;
    const el = sheetEl;
    if (!el?.hidden) return;
    if (listeSpielt()) warteBisStill(0);
    else if (stilleBlicke + 1 >= 2) leere(el);
    else warteBisStill(stilleBlicke + 1);
  }, LEEREN_NACHSEHEN_MS);
}

function leere(el: HTMLElement): void {
  el.querySelector(".alltag-list")?.replaceChildren();
  listenSpieler.clear();
  // Die Signatur gehört zur gezeichneten Liste — ohne sie zeichnet der nächste Abruf neu.
  lastSignature = "";
}

/**
 * Pre.1017: Sofort-Aufnahme-Overlay (vom Widget-Deep-Link `pawbot://aufnehmen`).
 * Startet die Aufnahme SOFORT, zeigt eine laufende Uhr + großen Stopp-Knopf —
 * kein extra „Starten". X = minimieren (Aufnahme läuft im Hintergrund weiter),
 * Stopp = beenden + speichern. DRY: gleiche Sheet-Bausteine + Recorder-API.
 * Im Browser/PWA (kein nativer Recorder) Fallback auf „Mein Alltag".
 */
export async function openRecorderOverlay(
  opts: { autoStopMs?: number; start?: boolean } = {},
): Promise<void> {
  closeForeignOverlays();
  // ⚠️ 22.09.2026 — vorher fiel der Browser hier immer auf die Liste
  //    zurueck und sah nie die laufende Uhr.
  if (!kannAufzeichnen()) {
    openAlltagSheet();
    return;
  }
  // ZUERST den echten Zustand vom nativen Recorder holen (überlebt Reloads),
  // damit die Entscheidung start/anzeigen + die Uhr stimmen.
  await syncFromNative();
  document.querySelector(".alltag-rec-overlay")?.remove();

  const el = document.createElement("div");
  el.className = "sheet sheet--modal alltag-rec-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");

  // Backdrop OHNE Klick-zum-Schließen — sonst verwaiste laufende Aufnahme.
  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  el.appendChild(backdrop);

  const card = document.createElement("div");
  card.className = "sheet-card alltag-rec-overlay-card";

  let ticker: number | null = null;
  let startAt = 0;
  let minimized = false;
  const stopTicker = (): void => {
    if (ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  };

  // Minimieren: Overlay zu, Aufnahme läuft im Hintergrund weiter (kein Auto-
  // Sprung in „Mein Alltag" beim Fertigstellen). Genutzt vom X UND vom
  // „App öffnen"-Knopf.
  const minimize = (): void => {
    minimized = true;
    stopTicker();
    el.remove();
  };
  card.appendChild(buildSheetHeader("Aufnahme", minimize));

  const statusEl = document.createElement("p");
  statusEl.className = "alltag-rec-overlay-status";
  statusEl.setAttribute("role", "status");
  statusEl.setAttribute("aria-live", "polite");
  statusEl.textContent = "Nimmt auf";

  const timeEl = document.createElement("div");
  timeEl.className = "alltag-rec-time";
  timeEl.textContent = "0:00";

  const stopBtn = document.createElement("button");
  stopBtn.type = "button";
  stopBtn.className = "alltag-rec-stop";
  stopBtn.textContent = "Stopp & speichern";
  // 2026-07-22 (Martin, "Stoppe ... und es passiert nichts"): Die WebView
  // friert im Hintergrund ein und verpasst die Zustands-Updates — dann
  // blieb das Sheet ewig auf "Stoppe ...", obwohl laengst gespeichert war.
  const stoppWatchdog = (): void => {
    window.setTimeout(() => {
      if (
        document.body.contains(el) &&
        statusEl.textContent === "Stoppe …"
      ) {
        statusEl.textContent = "Prüfe Speicherstand …";
        void syncFromNative();
      }
    }, 15_000);
  };
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "visible" &&
      document.body.contains(el) &&
      (statusEl.textContent === "Stoppe …" ||
        statusEl.textContent === "Prüfe Speicherstand …")
    ) {
      void syncFromNative();
    }
  });
  stopBtn.addEventListener("click", () => {
    // Sofortiges Feedback: Uhr einfrieren + „Stoppe …", damit es nicht
    // weiterzulaufen scheint, während der Recorder das letzte Stück abschließt.
    stopTicker();
    statusEl.textContent = "Stoppe …";
    stopBtn.disabled = true;
    stoppWatchdog();
    void (async () => {
      if (await stopRecordingForUser()) hinweisAufnahmeHaelt();
    })();
  });

  // Pause/Fortsetzen (2026-06-28): nutzt das native pause/resume
  // (pauseRecorderForUser/resumeRecorderForUser) → pausiert Aufnahme + Upload. Sinnvoll bei langen
  // passiven Aufnahmen. Bei der getimten Onboarding-Aufnahme ausgeblendet.
  // Seit 27.09. (Ticket 2708) pausiert er nur nach Halten, wie die Karte.
  const pause = baueHaltePauseKnopf("alltag-rec-pause-toggle", pauseGriff);
  const pauseBtn = pause.knopf;
  if (opts.autoStopMs) pauseBtn.hidden = true;

  // Normaler Knopf: in die App gehen, OHNE die Aufnahme zu beenden (läuft im
  // Hintergrund weiter; der Widget-Timer zeigt sie weiter an).
  const openAppBtn = document.createElement("button");
  openAppBtn.type = "button";
  openAppBtn.className = "alltag-rec-openapp";
  openAppBtn.textContent = "App öffnen — Aufnahme läuft weiter";
  openAppBtn.addEventListener("click", minimize);

  // Start-Knopf (2026-06-28, Martins Wunsch): Beim Oeffnen via Widget/manuell
  // startet die Aufnahme NICHT sofort — erst dieser Knopf loest sie aus, damit
  // ein versehentlicher Tipp keine ungewollte Aufnahme beginnt.
  const startBtn = document.createElement("button");
  startBtn.type = "button";
  startBtn.className = "alltag-rec-start";
  startBtn.textContent = "Aufnahme starten";
  startBtn.hidden = true;
  startBtn.addEventListener("click", () => {
    startBtn.disabled = true;
    // Gegenstueck zu Stop (zweiter bewusster Start-Weg): War durchgehende
    // Aufnahme gewuenscht, tritt die Absicht wieder in Kraft.
    void (async () => {
      await startRecording(cb);
      try {
        const { loadSettings } = await import("./settings.js");
        if (loadSettings().alwaysRecord === true) {
          await setAlwaysDayRecording(true);
        }
      } catch {
        /* Einstellungen nicht lesbar — dann bleibt es bei dieser Sitzung. */
      }
    })();
  });

  // „Bereit"-Zustand: Overlay offen, Aufnahme NICHT gestartet (nur Start-Knopf).
  const showReady = (): void => {
    el.classList.remove("is-recording");
    statusEl.textContent = "Bereit — tippe „Aufnahme starten“";
    // 2026-07-14 (Martin, oranger Punkt + 0:00): den iOS-Mikro-Punkt genau
    // HIER erklaeren — er bedeutet Mikro-BEREITSCHAFT (schnelles Sprechen),
    // nicht Aufnahme. Nimmt den Stress aus dem Widerspruch.
    proofEl.hidden = false;
    proofEl.classList.remove("is-warn");
    proofEl.textContent =
      "Oranger Punkt oben? Nur Mikro-Bereitschaft fürs Sprechen — aufgenommen wird erst nach dem Start.";
    timeEl.textContent = "0:00";
    startBtn.hidden = false;
    startBtn.disabled = false;
    stopBtn.hidden = true;
    pauseBtn.hidden = true;
    openAppBtn.hidden = true;
  };

  // 2026-07-14 (Martin): BEWEIS-Zeile — zeigt, wann das letzte Haeppchen
  // wirklich gesichert wurde (Serverfluss), statt nur ein Zustands-Flag.
  const proofEl = document.createElement("p");
  proofEl.className = "alltag-rec-proof";
  proofEl.hidden = true;
  card.append(statusEl, timeEl, proofEl, startBtn, pauseBtn, stopBtn, openAppBtn);
  el.appendChild(card);
  document.body.appendChild(el);

  const applyState = (s: RecorderState): void => {
    el.classList.toggle("is-recording", s === "recording" || s === "paused");
    // Sobald die Aufnahme laeuft/pausiert/abschliesst: Start-Knopf weg,
    // Stopp/App-oeffnen sichtbar (Pause nur ohne autoStopMs).
    if (s === "recording" || s === "paused" || s === "finishing") {
      startBtn.hidden = true;
      stopBtn.hidden = false;
      openAppBtn.hidden = !!opts.autoStopMs;
      pauseBtn.hidden = !!opts.autoStopMs;
    }
    if (s === "recording") {
      // 2026-07-14 (Martin, \"Uhr lief in der Pause weiter\"): die Engine
      // verschiebt startedAt beim Fortsetzen um die Pausendauer — beim
      // (Wieder-)Anlaufen IMMER frisch vom nativen Recorder lesen, damit die
      // Uhr die AUFGENOMMENE Zeit zeigt (nicht Wanduhr seit Start).
      // Frisch vom NATIVEN Recorder holen (syncFromNative aktualisiert den
      // JS-Cache) — der lokale Wert ist nach einem Resume veraltet.
      void syncFromNative().then(() => {
        const ns = getRecordingStartedAt();
        if (ns > 0) startAt = ns;
      });
      const nativeStart = getRecordingStartedAt();
      if (nativeStart > 0) startAt = nativeStart;
      if (!startAt) startAt = Date.now();
      if (ticker === null) {
        ticker = window.setInterval(gesteuert("live", () => {
          timeEl.textContent = formatClock((Date.now() - startAt) / 1000);
          // Beweis-Zeile: zeigt, dass die Aufnahme wirklich gesichert wird.
          //
          // 2026-07-29 (Audit C): Die Schwelle lag bei 50 SEKUNDEN — aus der
          // Zeit, als jedes Haeppchen sofort hochgeladen wurde. Seit dem
          // Buendeln (alle zehn Minuten) stand die Zeile rund neun von zehn
          // Minuten auf "Achtung", obwohl alles in Ordnung war. Dazu lief
          // alle zehn Sekunden ein Abgleich mit dem nativen Teil.
          //
          // Wichtiger noch als die Zahl: Die Formulierung war irrefuehrend.
          // "Nichts gesichert" stimmte nie — die Haeppchen werden sofort auf
          // dem Geraet geschrieben, nur das SENDEN ist gebuendelt. Verloren
          // geht dabei nichts; es liegt nur noch nicht auf dem Server.
          const up = getUploadInfo();
          const at = getLastUploadAt();
          proofEl.hidden = false;
          if (at > 0) {
            const secs = Math.round((Date.now() - at) / 1000);
            const min = Math.round(secs / 60);
            // Erst jenseits des Sende-Fensters plus Luft ist etwas faul.
            const verdaechtig = secs > 15 * 60;
            proofEl.classList.toggle("is-warn", verdaechtig);
            // Zombie-Schutz: nur noch alle 60 s abgleichen statt alle 10 —
            // und erst, wenn es wirklich auffaellig lange still ist.
            if (verdaechtig && up.pending === 0 && secs % 60 === 0) {
              void syncFromNative();
            }
            proofEl.textContent = verdaechtig
              ? `Achtung: seit ${min} Min nichts übertragen (${up.pending} offen)`
              : up.pending > 0
                ? `${up.uploaded} Häppchen übertragen · ${up.pending} warten auf die nächste Sendung`
                : `${up.uploaded} Häppchen übertragen · zuletzt vor ${min} Min`;
          } else {
            proofEl.classList.remove("is-warn");
            proofEl.textContent =
              "Aufnahme läuft — die erste Sendung kommt in wenigen Minuten";
          }
          // Onboarding-Aha-Moment: automatisch stoppen, damit der Nutzer nicht
          // selbst den Stopp-Knopf suchen muss (er erwartet eine getimte Aufnahme).
          if (opts.autoStopMs && Date.now() - startAt >= opts.autoStopMs) {
            stopTicker();
            statusEl.textContent = "Stoppe …";
            stopBtn.disabled = true;
            void stopRecording("onboarding-auto-stopp");
          }
        }, true), 1000);
      }
      statusEl.classList.remove("is-wartet", "is-pause");
      statusEl.textContent = opts.autoStopMs
        ? "Nimmt auf — ich stoppe automatisch, oder tippe Stopp"
        : laufText("Nimmt auf");
      stopBtn.disabled = false;
      pause.zeige("pause");
    } else if (s === "paused") {
      stopTicker();
      // 2026-07-14 (Martin, "0:00 obwohl schon 10s"): die Uhr auf den
      // Pausen-Moment einfrieren — auch wenn das Overlay erst MITTEN in
      // der Pause geoeffnet wird (der Ticker lief dann nie).
      if (startAt > 0) {
        const pa = getRecordingPausedAt();
        const frozenMs = Math.max(0, (pa > 0 ? pa : Date.now()) - startAt);
        timeEl.textContent = formatClock(frozenMs / 1000);
      }
      statusEl.textContent = pauseText(offenePausenGruende(), "Pausiert");
      statusEl.classList.add("is-pause");
      const anzeige = pauseAnzeige(offenePausenGruende());
      statusEl.classList.toggle("is-wartet", anzeige === "wartet" || anzeige === "unbekannt");
      if (anzeige === "wartet" || anzeige === "unbekannt") weckeBeiUnfreiwilligerPause();
      proofEl.hidden = false;
      proofEl.classList.remove("is-warn");
      proofEl.textContent =
        "Pause — es wird NICHTS aufgenommen; Uhr steht.";
      pause.zeige("fortsetzen");
    } else if (s === "finishing") {
      stopTicker();
      statusEl.textContent =
        "Wird verarbeitet — Transkript + Zusammenfassung …";
      stopBtn.disabled = true;
      markRecordingProcessing();
    } else if (s === "idle") {
      stopTicker();
      el.remove();
      // Fertig → das Ergebnis in „Mein Alltag" zeigen (außer der Nutzer hat
      // vorher minimiert; dann läuft/lief es bewusst im Hintergrund).
      if (!minimized) openAlltagSheet();
    } else if (s === "error") {
      stopTicker();
      statusEl.textContent = "Fehler — erneut versuchen";
      stopBtn.disabled = false;
    }
  };

  const cb = {
    onState: applyState,
    // P1 (Martin, 'wieviel kam an?'): live zeigen, was WIRKLICH uebertragen
    // ist — nicht nur, dass aufgenommen wird.
    onUploadProgress: (info: { pending: number; uploaded: number }) => {
      const st = getRecorderState();
      if (st !== "recording" && st !== "paused") return;
      const base =
        st === "paused" ? pauseText(offenePausenGruende(), "Pausiert") : laufText("Nimmt auf");
      statusEl.textContent =
        info.pending > 0
          ? `${base} · ${info.uploaded} übertragen · ${info.pending} offen`
          : `${base} · ${info.uploaded} übertragen`;
    },
    onError: (err: Error) => {
      statusEl.textContent = `Fehler: ${err.message}`;
    },
  };
  // ZUSTANDS-BEWUSST: läuft schon eine Aufnahme, NICHT neu starten — nur die
  // laufende anzeigen (mit korrekter Uhr aus dem zentralen Start-Zeitstempel).
  const st = getRecorderState();
  if (st === "recording" || st === "paused" || st === "finishing") {
    // Läuft schon (oder wird gerade verarbeitet) → anzeigen, NICHT neu starten.
    startAt = getRecordingStartedAt() || Date.now();
    void observeRecorder(cb);
    applyState(st);
    /*
     * 07.09.2026 — steht sie auf Pause, heisst „aufnehmen" fortsetzen.
     *
     * Widget-Mikro und Zuhause-Kachel schicken `start: true`; das ist die
     * Handlung „ich will jetzt aufnehmen". Bisher zeigte das Blatt in
     * diesem Fall nur „Pausiert" und wartete auf einen zweiten Griff — auf
     * genau den Knopf, der bis heute still scheiterte. Neu starten waere
     * falsch (die laufende Sitzung ginge verloren), also derselbe Weg wie
     * beim Knopf: eine Nutzerhandlung, die fremde Gruende raeumt.
     */
    if (st === "paused" && opts.start)
      void raeumeTonFuerAufnahme().then(() => resumeRecorderForUser());
  } else if (opts.autoStopMs || opts.start) {
    // Widget-Mikro und Onboarding starten sofort. Ohne das blieb
    // das Widget auf Idle, obwohl der Nutzer „Aufnehmen“ getippt hat.
    //
    // 2026-08-23 (Martin: "es läuft augenscheinlich, aber in der Notch sehe ich
    // nicht das Aufnahme-Symbol"): Hier stand `void startRecording(cb)` — das
    // warf Ergebnis UND Fehler weg. Kam der Deep-Link vom Widget beim
    // Kaltstart, war das native Plugin noch nicht da, der Start scheiterte
    // still, und dieses Fenster zeigte trotzdem „Nimmt auf" mit laufender Uhr.
    //
    // Bis der Start bestätigt ist, sagt die Statuszeile die Wahrheit.
    statusEl.textContent = "Mikro wird geöffnet …";
    // 15.09.2026: Laeuft der Spiegel, tritt er zurueck — sonst startet auf
    // iOS nichts, und das Blatt zeigt „Nimmt auf" mit stehender Uhr.
    void raeumeTonFuerAufnahme()
      .then(() => startRecording(cb))
      .then(() => {
      if (getRecorderState() !== "recording") {
        // Der Start kam nicht zustande. Nichts vortäuschen — zurück auf den
        // Bereit-Zustand mit sichtbarem Start-Knopf.
        stopTicker();
        showReady();
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: {
              message:
                "Die Aufnahme konnte nicht starten. Tipp auf „Aufnahme starten“.",
              kind: "warn",
              ms: 6000,
            },
          }),
        );
      }
    });
  } else {
    showReady();
  }
}
